/** Domain models + reference data + helpers for the Hilbertech Hotel B2B Portal.
 *  All business data (hotels, reservations, agencies, wallet, etc.) comes from the real
 *  backend — see hotel-sourcing.service.ts, agency-data.service.ts, wallet.service.ts. */

import { Lang } from './services/language.service';

/* ----------------------------- Types ----------------------------- */
// 'MIXED' is hotel-level only (HotelOffer.source) — a single RatePlan/Reservation always has one
// real connector, never mixed; only the aggregate hotel-level badge can represent a blend.
export type Source = 'EXTRANET' | 'AMADEUS' | 'BEDBANK' | 'MIXED';
// Query-time source selection (feature: hotel-b2b-source-selection) — which connector(s) a search
// actually asks, distinct from `Source` above (which tags where a result CAME from). Extranet is
// split into Hotel (direct/marketplace contracts) vs Allotment (agency-published inventory,
// entitlement-gated — see hotel-connector.service.ts) since an agent can choose them independently.
export type SearchSource = 'AMADEUS' | 'EXTRANET_HOTEL' | 'EXTRANET_ALLOTMENT';
export type Board = 'RO' | 'BB' | 'HB' | 'FB' | 'AI';
// REQUESTED = paid (B2C master order), parked for the back-office fulfilment decision:
// confirm the drafted offer or re-source a different one from the fresh board.
export type BookingStatus = 'BOOKED' | 'PENDING' | 'CANCELLED' | 'FAILED' | 'DRAFT' | 'REQUESTED';
export type Role = 'AGENCY_MANAGER' | 'AGENT' | 'ADMIN';

export interface RatePlan {
  ratePlanId: string;
  // Source-blind booking identity (feat: hotel-booking-identity-source-blind) — a UUID minted by
  // be-flight-search-service for EVERY offer (both sources) and resolved server-side by
  // be-flight-reservation-service at booking time. When every selected line carries one, booking
  // sends ONE offerSelections body and never branches on `source` client-side (that field stays
  // display-only: badges/scope). Optional only for legacy data (old sessionStorage snapshots /
  // pre-rollout search responses) — those lines take the legacy cacheId/extranetLines fallback.
  bookingOfferId?: string;
  board: Board;
  refundable: boolean;
  // Resolved connector-side (WI #1098) — the calendar date after which cancellation is no longer
  // free, for both sources. null when the source never set a deadline for this rate plan.
  deadlineDate: string | null;
  // NATIVE per-night price, in the SOURCE's own currency (feature: hotel-reservation-currency —
  // the connectors no longer convert: Amadeus prices in EUR, an Extranet property in whatever its
  // owner set). Kept in the data model for the back office/ledger but NEVER rendered in the
  // agent-facing flow (design decision A): every price surface reads resNightly below instead.
  // Unrounded (stay total / nights): a display figure only — totals come from stayTotal.
  nightly: number;
  // NATIVE per-room STAY total exactly as the source priced it (the offer's totalPrice) — the
  // figure the BE multiplies by the room count. Optional only for plans held from before WI 3976.
  stayTotal?: number;
  // Per-night price restated in the agency's RESERVATION currency (the search's `currency` param),
  // derived from the offer's `reservationTotal`. Unrounded since WI 3976: it is only ever shown
  // (to the currency's minor unit, see money()), never multiplied back into a total — a whole-unit
  // rounded nightly × 30 nights drifted up to ~15 per room from what the wallet is charged.
  // Three-state by design:
  //  - number   → converted price, what the agent sees everywhere (decision A);
  //  - null     → the platform FX engine genuinely cannot price this offer in the requested
  //               currency right now (§4 "FX unavailable") — the offer is still shown, flagged,
  //               never silently dropped or booked;
  //  - undefined → response predates this contract (old sessionStorage snapshot) — fall back to
  //               the native nightly, which the old BE had already converted. See planNightly().
  resNightly?: number | null;
  // Per-room STAY total in the reservation currency — the BE's `reservationTotal`, verbatim
  // (WI 3976). Every stay, line and checkout total is built from this × rooms, never from a
  // per-night figure (see lineTotalsOf() for how close that is to the debit). Same three-state
  // contract as resNightly; undefined (a plan held from before this field) falls back to
  // resNightly × nights, see planStayTotal().
  resStayTotal?: number | null;
  // Currency code the res* amounts are denominated in (the reservation currency echoed by the BE).
  resCurrency?: string;
  // Real inventory count (WI #1098), Extranet-only — null for Amadeus, which has no availability
  // field anywhere in its offers API surface. null must never be treated as "0 left".
  roomsLeft: number | null;
  cacheId?: string;      // AMADEUS only: opaque offer id for GET /hotels/offers/{cacheId}/price
  // Per-offer source — a single (merged) HotelOffer can hold plans from both connectors, so this
  // is authoritative for booking/cancel/refresh dispatch, unlike the hotel-level HotelOffer.source.
  source: Source;
  extranetPropertyId?: string; // EXTRANET only: needed to book/refresh directly against the Extranet
  // EXTRANET only (feature: hotel-b2b-source-selection) — 'MARKETPLACE' (direct hotel-owned
  // contracts, "Extranet · Hotel") or 'PRIVATE' (agency-published allotment inventory,
  // "Extranet · Allotment"). Already on the wire as HotelOfferDto.extranetScope; undefined for
  // Amadeus plans and for any Extranet plan whose scope wasn't set. See hotelExtranetScope() below.
  extranetScope?: 'MARKETPLACE' | 'PRIVATE';
}
export interface HotelRoom {
  roomTypeId: string;
  room: string;
  bed: string;
  bedCount?: number;       // Amadeus-only (typeEstimated.beds via roomBedCount, WI #1095) — display-only, e.g. "2 × Twin"
  // Real per-room capacity (WI #1098), Extranet-only — null for Amadeus, which has no per-offer
  // occupancy field anywhere in this API surface. null must never fall back to a fabricated number.
  maxOcc: number | null;
  size?: number;          // m²
  description?: string;
  photos?: string[];
  highlights?: string[];  // curated highlight amenity codes (icon+label via AMENITY_*)
  services?: string[];    // full facility codes for the "In this room" checklist
  plans: RatePlan[];
}
export interface HotelOffer {
  id: string;
  name: string;
  city: string;
  country: string;
  stars: number;
  source: Source;
  rating: number;
  address: string;
  lat: number;
  lng: number;
  amenities: string[];
  description?: string;
  distanceKm?: number; // distance from the search coordinates (geo proximity search)
  photos: string[];
  rooms: HotelRoom[];
  // Offer-detail extension of hotel-b2b-source-selection — the EFFECTIVE (post-entitlement-clamp)
  // sources the last offers/refresh call actually queried, same reconcile-with-reality purpose as
  // HotelSearchResult#querySources above. undefined until a real refresh response has set it (the
  // initial getOffer()/search-derived HotelOffer never carries this).
  querySources?: SearchSource[];
}
/** search()'s return shape — carries the connector's real totalCount alongside the page of
 *  hotels, so callers can offer "Load more" instead of being capped at one page forever.
 *  `hasMore`/`totalCountIsExact` are v2 (server-side filters) only — be-flight-search-service's
 *  v2 `/hotels/v2/search/by-geo` sets both; the v1 endpoint (ExtranetHotelSourcing, and any old
 *  cached response) never does, so callers must treat their absence as "unknown / assume exact,
 *  fall back to the old totalCount-vs-all().length inference" rather than throwing. */
export interface HotelSearchResult {
  hotels: HotelOffer[];
  totalCount: number;
  hasMore?: boolean;
  totalCountIsExact?: boolean;
  // v2 ONLY, same undefined-when-not-applicable convention as the pair above — the [min,max]
  // nightly price actually seen across this response's offers (be-flight-search-service's
  // HotelSearchServiceV2 computes it from the same offers hasMore/totalCountIsExact describe),
  // used by search.component.ts to derive the price-filter slider's bounds from real data instead
  // of a static per-currency guess.
  priceRange?: { min: number; max: number };
  // v2 ONLY — the EFFECTIVE sources the backend actually queried after its own entitlement clamp
  // (be-flight-search-service's HotelSearchServiceV2#resolveEffectiveSources), which can differ
  // from the `sources` the request asked for (e.g. a non-entitled agency's EXTRANET_ALLOTMENT
  // selection is silently dropped) — search.component.ts uses this to reconcile its toggle state
  // with reality rather than trusting what it sent.
  querySources?: SearchSource[];
}
/** One row of the hotel-name autocomplete over the searched area (be-flight-search-service
 *  `GET /hotels/v2/suggest`, WI #3267). `hotelId` is what `searchByHotelIds()` prices back and the
 *  same `id` a landed HotelOffer carries (the candidate's own id — an 8-char Amadeus code, or the
 *  Extranet property UUID of a standalone Extranet hotel — never a folded twin). `source` mirrors
 *  the card's badge; `distanceKm` is from the searched point, one decimal; `cityName`/`starRating`
 *  are best-effort (Extranet content carries them, an Amadeus list row mostly does not). */
export interface HotelNameSuggestion {
  hotelId: string;
  name: string;
  source: 'AMADEUS' | 'EXTRANET';
  latitude?: number | null;
  longitude?: number | null;
  distanceKm?: number | null;
  cityName?: string | null;
  starRating?: number | null;
}
export interface HotelSearchCriteria {
  dest: string;      // city NAME — shown in the field, sent for logging/fallback
  destCode?: string; // IATA city/airport code from the lookup — carried for Phase-2 Amadeus
  lat?: number;      // latitude of the chosen city — drives the Extranet geo-proximity search
  lng?: number;      // longitude of the chosen city
  radiusKm?: number; // search radius around lat/lng (defaults applied server-side)
  checkIn: string;   // ISO date
  checkOut: string;
  rooms: number;
  adults: number;
  children: number;    // total incl. infants — FE-only, not sent to the search request (see toSearchPayload)
  childAges: number[]; // length === children, each 0-17; age < 2 = infant
  // v2 ("server-side filters") request params ONLY — undefined/empty = no filter, mirroring the
  // BE's own HotelSearchCriteria.refundable() null-means-unfiltered convention. Populated by
  // search.component.ts from the stars()/boardFilter()/offerType() signals right before a
  // request goes out (see its withLiveFilters()), then carried on `applied` for critKey()/
  // dirty() and for loadMore() to replay the same filters on later pages. See
  // hotel-sourcing.service.ts's FlightSearchHotelSourcing.search() for the exact wire translation.
  stars?: number[];    // selected star ratings, e.g. [4, 5] — sent as the BE's `ratings` (List<String>)
  // Exactly ONE board bucket — the BE's v2 boardType filter does a single equalsIgnoreCase match
  // (HotelPostMergeFilter), it cannot OR several values. search.component.ts only ever sets this
  // when the user has exactly one board checkbox checked; 2+ checked forwards no board filter at
  // all rather than silently picking one of the user's selections and dropping the rest.
  boardType?: Board;
  refundable?: boolean; // true = refundable-only, false = non-refundable-only, undefined = both/no filter
  // Nightly price bounds, same null/undefined-means-unfiltered convention as refundable above —
  // sent as the BE's minPrice/maxPrice (HotelPostMergeFilter.filterByPrice()). Both initialized
  // from the search response's own priceRange (see HotelSearchResult) rather than a fixed
  // per-currency constant, so the slider always starts at what's actually available.
  minPrice?: number;
  maxPrice?: number;
  // Which connector(s) to actually query — undefined/empty = all (identical to today's always-
  // query-everything behavior, backward compatible). Populated by search.component.ts from its
  // sources() signal (see withLiveFilters()); a non-entitled agency's EXTRANET_ALLOTMENT
  // selection is clamped server-side, never trusted client-side alone (see querySources above).
  sources?: SearchSource[];
}
export interface SelectedRate extends RatePlan {
  room: string;
  roomTypeId: string;
  bed: string;
  maxOcc: number | null;
  rooms: number;     // quantity chosen by the user (1..min(crit.rooms, roomsLeft))
  total: number;     // NATIVE line total = stayTotal × rooms (never rendered, decision A)
  // Line total in the reservation currency = resStayTotal × rooms (see lineTotalsOf()). Same
  // three-state contract as RatePlan.resNightly (null = FX unavailable → checkout blocks, §4).
  resTotal?: number | null;
}
export interface Reservation {
  id: string;
  hotel: string;
  city: string;
  source: Source;
  status: BookingStatus;
  checkIn: string;
  checkOut: string;
  rooms: number;
  board: Board;
  guest: string;
  agency: string;
  refundable: boolean;
  deadline: string | null;
  total: number;     // in `currency`, as confirmed by the booking provider
  currency: string;
  guestEmail?: string;
  agencyEmail?: string;
  agencyPhone?: string;
  // Provider (Amadeus) booking references — the refs an agent actually quotes to the hotel.
  pnr?: string;
  providerOrderId?: string;
  providerConfirmationId?: string;
  hotelCode?: string;      // provider hotel id (e.g. Amadeus property code)
  // Booking timeline — drives the per-booking Transactions card.
  createdAt?: string;      // ISO instant of the wallet debit (booking time)
  refundedAt?: string;     // ISO instant of the wallet refund (cancellation time)
  refundAmount?: number;   // refunded amount, same currency as `total`
  // Display snapshot captured at booking (immutable — what the agent saw when booking).
  address?: string;
  stars?: number;
  lat?: number;
  lng?: number;
  photoUrl?: string;
  roomTypeName?: string;
  bedType?: string;
  numberOfBeds?: number;
  maxOccupancy?: number;
  lines?: ReservationLine[];
}

export interface ReservationLine {
  roomTypeId?: string;
  ratePlanId?: string;
  roomName?: string;
  board?: string;
  rooms?: number;
  lineTotal?: number;
}
export interface Agency {
  id: string; name: string; head: boolean; parent: string | null;
  city: string; country: string; vat: string; members: number; currency: string;
  email?: string; phone?: string;   // agency contact (#32)
  /** Agency logo, bare base64 (no `data:` prefix) + original filename, round-tripped through
   *  PUT /agencies/{id}. Use logoDataUri() to render it. */
  logoBase64?: string; logoName?: string;
}

/** "City, CC" — but only the parts that exist. Agencies seeded without an address were
 *  rendering as a bare ", " (verified live against be-agency-service, whose records may carry
 *  neither city nor country), which reads as a broken field rather than an empty one. */
export function cityLine(a: Pick<Agency, 'city' | 'country'> | null | undefined): string {
  return [a?.city, a?.country].map(v => (v ?? '').trim()).filter(Boolean).join(', ') || '—';
}

/** The agency logo as a browser-renderable data URI, or null when unset. The BE stores a bare
 *  base64 payload; it is uploaded as JPEG (see AgencyDataService's logo compression). */
export function logoDataUri(a: Pick<Agency, 'logoBase64'> | null | undefined): string | null {
  const b64 = a?.logoBase64;
  if (!b64) return null;
  return b64.startsWith('data:') ? b64 : `data:image/jpeg;base64,${b64}`;
}
export interface Member { name: string; email: string; role: Role; status: 'Active' | 'Invited'; }
export interface Balance { id: string; currency: string; amount: number; plafond: number; }
export interface Transaction {
  id: string; ref: string; type: string;
  amount: number; currency: string; status: 'Completed' | 'Pending' | 'Failed'; date: string;
}
export type TopUpStatus = 'PENDING' | 'APPROVED' | 'REJECTED';
export interface WalletTopUp {
  id: string;
  amount: number;
  currency: string;
  status: TopUpStatus;
  /** Free-text reference + (fake, filename-only) proof-of-payment, and any rejection note
   *  appended — be-agency-service's generic Transaction has one description field, not
   *  separate reference/proof/note columns (see agency-api.service.ts). */
  description?: string;
  requestedBy?: string;
  createdAt?: string | null;
  /** 'BANK_TRANSFER' (manual proof + manager approval) or 'CREDIT_CARD' (real-time Hosted
   *  Payment Page via Revolut, settled by polling, not a manager's call) — drives which row
   *  action (Approve/Reject vs Check status) the ledger shows for a PENDING row. */
  chargeType?: string;
}
export interface Margin { id?: string; scope: string; kind: 'HOTEL'; type: 'Percentage' | 'Fixed'; value: number; currency: string; }
export interface AppUser { name: string; email: string; role: Role; agency: string; initials: string; }

/* ----------------------------- Helpers ----------------------------- */
export const CURRENCY_SYMBOL: Record<string, string> = { EUR: '€', USD: '$', GBP: '£' };

/** Every currency be-agency-service can open a wallet in — mirrors its `Currency` enum.
 *  Single source of truth: wallet.component.ts and sub-agency.component.ts each used to keep
 *  their own verbatim copy of this list, and the agency forms offered a third, shorter one
 *  (EUR/USD/GBP only), so the same app disagreed with itself about which currencies exist. */
export const ALL_CURRENCIES = [
  'EUR', 'USD', 'SAR', 'AED', 'GBP', 'JOD', 'EGP', 'CAD', 'CHF',
  'QAR', 'KWD', 'BHD', 'TRY', 'PLN', 'NOK', 'DKK', 'SEK',
] as const;

/** Full names for the base-currency picker (#5.1), which lists `CCY — Full Name`. */
export const CURRENCY_NAME: Record<string, string> = {
  EUR: 'Euro', USD: 'US Dollar', SAR: 'Saudi Riyal', AED: 'UAE Dirham', GBP: 'British Pound',
  JOD: 'Jordanian Dinar', EGP: 'Egyptian Pound', CAD: 'Canadian Dollar', CHF: 'Swiss Franc',
  QAR: 'Qatari Riyal', KWD: 'Kuwaiti Dinar', BHD: 'Bahraini Dinar', TRY: 'Turkish Lira',
  PLN: 'Polish Zloty', NOK: 'Norwegian Krone', DKK: 'Danish Krone', SEK: 'Swedish Krona',
  // Not in ALL_CURRENCIES (which is short of the BE `Currency` enum by exactly this one), but it
  // does come back from GET /currencies/catalog, so a network that adds it must not be left
  // reading a bare code in the Currencies table.
  MAD: 'Moroccan Dirham',
};

/** Display name for a currency code, over the shared CURRENCY_NAME map above. Falls back to
 *  the bare ISO code so an unknown wallet is never blocked from rendering. */
export function currencyName(code: string): string { return CURRENCY_NAME[code] ?? code; }

/* ------------------- Currency margins (README §6, root agency only) -------------------
 * One row per platform currency, as resolved by be-agency-service's
 * GET /agencies/{id}/currency-margins/effective. Every rate on the wire is EUR-ANCHORED
 * ("1 EUR = ratePerEur {currency}") whatever the network's base currency is; the page re-anchors
 * them for display via marketRateOf(). One rate set governs the whole network — there is no
 * per-sub-agency override, by design (decision B). */
export interface CurrencyMargin {
  currency: string;
  /** Network margin in percent, −10..+10. 0 when no margin row exists yet (sourceMarginId null). */
  percentage: number;
  /** false = the rate is pinned at pinnedRatePerEur and the daily feed no longer moves it. */
  autoUpdate: boolean;
  pinnedRatePerEur: number | null;
  pinnedAt: string | null;
  liveMarketRatePerEur: number | null;
  /** The feed genuinely cannot price this currency right now. Must render as its own state —
   *  never as "0%" and never as a stale number (same no-silent-fallback rule as §4). */
  marketRateUnavailable: boolean;
  effectiveRatePerEur: number | null;
  /** null = no margin row exists yet for this currency ("Add margin" rather than "Edit"). */
  sourceMarginId: string | null;
}
/** One entry of a currency's change history (§6.3). MARGIN and RATE changes share one feed and
 *  are told apart by `kind` — they read differently and move different numbers. */
export interface CurrencyMarginChange {
  id: string;
  currency: string;
  kind: 'MARGIN' | 'RATE';
  oldPercentage: number | null;
  newPercentage: number | null;
  oldRatePerEur: number | null;
  newRatePerEur: number | null;
  changedByEmail: string;
  changedAt: string;
}
/* ------------- Currency catalogue (feature: hotel-currencies-add-currency §2) -------------
 * GET /currencies/catalog — every currency the platform supports, i.e. every currency the network
 * COULD quote. Deliberately a superset of the quoted set that GET .../currency-margins/effective
 * returns: the difference between the two is exactly what the Add Currency picker offers, and an
 * empty difference is what disables the CTA. */
export interface CurrencyCatalogEntry {
  code: string;
  name: string;
  symbol: string;
  fractionDigits: number;
  /** false = the platform FX feed does not cover this currency yet. It can still be added — it
   *  then behaves exactly like any feed-down row (Unavailable, no effective rate, not bookable)
   *  and recovers on its own (decision B) — but the picker and the dialog warn first. */
  feedAvailable: boolean;
  /** EUR-anchored live feed rate, for the dialog's "Market rate today" preview. The currency is
   *  not quoted yet, so it has no row in CurrencyMargin[] to read a rate off — the catalogue is
   *  the only place it can come from. Optional because it is an addition to the contract as first
   *  drafted; when it is absent the preview says the rate arrives with the feed rather than
   *  inventing one. */
  liveMarketRatePerEur: number | null;
}
/** The EUR-anchored rate actually in force for one currency: the pinned figure while auto-update
 *  is off, the live feed value otherwise. `null` = feed down (§6 "Unavailable"). EUR is 1 by
 *  definition of the anchor, even when the BE returns no row for it (the base currency has no row
 *  of its own when it IS the base). */
export function ratePerEurOf(rows: CurrencyMargin[], code: string): number | null {
  const row = rows.find(r => r.currency === code);
  if (!row) return code === 'EUR' ? 1 : null;
  if (!row.autoUpdate && row.pinnedRatePerEur != null) return row.pinnedRatePerEur;
  return row.marketRateUnavailable ? null : row.liveMarketRatePerEur;
}
/** Market rate as the page displays it — re-anchored on the network's base currency, i.e.
 *  "1 {base} = X {currency}" = feed(currency) / feed(base). null when either side is unpriced. */
export function marketRateOf(rows: CurrencyMargin[], code: string, base: string): number | null {
  const v = ratePerEurOf(rows, code);
  const b = ratePerEurOf(rows, base);
  return v == null || b == null || b === 0 ? null : v / b;
}
/** What the daily feed says right now for one currency, IGNORING any pin — shown next to the
 *  input in the manual-rate dialog so the manager can see the value they are overriding. */
export function liveMarketRateOf(rows: CurrencyMargin[], code: string, base: string): number | null {
  const raw = (c: string): number | null => {
    const row = rows.find(r => r.currency === c);
    if (!row) return c === 'EUR' ? 1 : null;
    return row.marketRateUnavailable ? null : row.liveMarketRatePerEur;
  };
  const v = raw(code);
  const b = raw(base);
  return v == null || b == null || b === 0 ? null : v / b;
}
/** Effective rate = market × (1 + margin%), computed live (never read back off the wire, so the
 *  edit dialog's preview and the table always agree). null when there is no market rate. */
export function effectiveRateOf(market: number | null, percentage: number): number | null {
  return market == null ? null : market * (1 + percentage / 100);
}
/** Converts what the manager typed in the pin dialog — "1 {base} = X {currency}" — into the
 *  EUR-anchored ratePerEur the pin endpoint stores. Anchoring on EUR rather than on the base is
 *  what keeps a pinned rate correct if the base currency is later changed. When the base IS EUR
 *  its feed value is 1, so the typed value already is the ratePerEur. */
export function pinnedPerEurFrom(typed: number, baseRatePerEur: number): number {
  return typed * baseRatePerEur;
}
/** Rates, unlike money, are shown to up to 4 decimals — a 2-decimal SAR rate would hide the
 *  difference a 0.5% margin makes. */
export function fmtRate(v: number | null | undefined, lang: Lang = 'en'): string {
  if (v == null) return '—';
  return v.toLocaleString(LOCALE[lang], { minimumFractionDigits: 2, maximumFractionDigits: 4 });
}
/** Signed percentage, the way both the table and the history read it: "+2.5%" / "−1.8%" / "0%". */
export function fmtSignedPct(v: number, lang: Lang = 'en'): string {
  const sign = v > 0 ? '+' : v < 0 ? '−' : '';
  return sign + Math.abs(v).toLocaleString(LOCALE[lang], { maximumFractionDigits: 2 }) + '%';
}
export const BOARD_LABEL: Record<Board, string> = {
  RO: 'Room Only', BB: 'Breakfast', HB: 'Half Board', FB: 'Full Board', AI: 'All Inclusive',
};
// Was hardcoded to a fixed 2026-06-27 demo date — every default search date (+30/+33 days) and
// deadline/past-date check (computeRefund, date-range-field) was frozen to that day forever
// regardless of when the app actually loaded. Real current date, captured once at module load.
export const TODAY = new Date();

// Per-language Intl locale for number/date formatting — single source of truth, also used by
// date-range-field.component.ts's own calendar/month labels.
export const LOCALE: Record<Lang, string> = { en: 'en-GB', it: 'it-IT', ar: 'ar-SA', es: 'es-ES', fr: 'fr-FR', de: 'de-DE' };

const FRACTION_DIGITS = new Map<string, number>();
/** Minor-unit digits of a currency (ISO 4217): 2 for EUR/USD/SAR…, 3 for JOD/KWD/BHD. Read from
 *  the runtime's own Intl currency data, so no hand-kept table can drift from it; 2 for a code
 *  Intl rejects (an empty or placeholder currency). */
export function currencyFractionDigits(currency: string): number {
  let digits = FRACTION_DIGITS.get(currency);
  if (digits === undefined) {
    try {
      digits = new Intl.NumberFormat('en', { style: 'currency', currency }).resolvedOptions().maximumFractionDigits ?? 2;
    } catch {
      digits = 2;
    }
    FRACTION_DIGITS.set(currency, digits);
  }
  return digits;
}
/** Booking amounts are BE decimals carried as JS numbers, and multiplying or adding them leaves
 *  binary noise (677.97 × 5 = 3389.8500000000004) that the strict wallet check (amount <=
 *  balance) could read as a hair over the balance. Snapping to 6 decimals gives back the exact
 *  decimal without touching any real minor unit (wallet currencies have at most 3). */
export function exactMoney(v: number): number {
  return Math.round(v * 1e6) / 1e6;
}

// `amount` is already denominated in `currency` — the backend now prices/reprices/books in
// whatever currency the request asked for, so this only formats, it no longer converts.
export function money(amount: number, currency = 'EUR', lang: Lang = 'en'): string {
  // Currencies without a dedicated symbol (e.g. SAR on Amadeus bookings) show their ISO code —
  // a bare unlabelled number reads as broken.
  const symbol = CURRENCY_SYMBOL[currency] ?? (currency ? currency + ' ' : '');
  // As many decimals as the currency has minor units (WI 3976): 3 for JOD/KWD/BHD. Hotel debits
  // are 2-decimal figures (they just gain a trailing 0), but a per-night share or a 3-decimal
  // amount entered elsewhere (e.g. a Fixed margin) must not be cut at 2.
  const digits = currencyFractionDigits(currency);
  return symbol + amount.toLocaleString(LOCALE[lang], { minimumFractionDigits: digits, maximumFractionDigits: digits });
}
export function nights(a: string, b: string): number {
  return Math.max(1, Math.round((+new Date(b) - +new Date(a)) / 86400000));
}
export function addDays(d: Date, n: number): Date { const x = new Date(d); x.setDate(x.getDate() + n); return x; }
export function iso(d: Date): string { return d.toISOString().slice(0, 10); }
// Both guard against missing/unparsable input — reservations that failed before the provider
// call carry no dates at all, and "Invalid Date" must never reach the screen.
export function fmtShort(s: string | null | undefined, lang: Lang = 'en'): string {
  const d = new Date(s ?? '');
  return isNaN(+d) ? '—' : d.toLocaleDateString(LOCALE[lang], { day: '2-digit', month: 'short' });
}
export function fmtLong(s: string | null | undefined, lang: Lang = 'en'): string {
  const d = new Date(s ?? '');
  return isNaN(+d) ? '—' : d.toLocaleDateString(LOCALE[lang], { day: '2-digit', month: 'short', year: 'numeric' });
}
/** The reservation reference to show an agent, e.g. to quote to the hotel or trace against
 *  Extranet. `pnr` is a GDS air-record-locator concept — the Amadeus hotel connector only fills
 *  it when the hotel order is linked to an air PNR (rare for a standalone hotel booking), so it's
 *  frequently empty even for a normal confirmed Amadeus booking. `providerConfirmationId` is the
 *  real hotel-facing confirmation number Amadeus returns for every confirmed booking and is what
 *  should be quoted to the property instead. Extranet bookings get neither back from the provider
 *  — be-flight-reservation-service's HotelReservationService.executeExtranetConsume() derives
 *  "HB-" + first 8 chars of the reservation id (uppercased) and sends *that* to Extranet as the
 *  booking's reference, but never returns it to this FE. Re-deriving it here with the same formula
 *  keeps what an agent sees in the B2B portal identical to what the property sees in Extranet for
 *  the same booking, without needing a BE change. */
export function bookingRef(r: Pick<Reservation, 'id' | 'pnr' | 'providerConfirmationId' | 'source'>): string {
  if (r.pnr) return r.pnr;
  if (r.providerConfirmationId) return r.providerConfirmationId;
  return r.source === 'EXTRANET' ? `HB-${r.id.slice(0, 8).toUpperCase()}` : r.id;
}
/** Effective per-night price of one rate plan, in the active reservation currency (feature:
 *  hotel-reservation-currency, decision A — the ONLY amount the agent ever sees).
 *  `undefined` resNightly (pre-contract cached response) falls back to the native nightly,
 *  which the old BE had already converted into the requested currency; `null` stays null —
 *  it means "genuinely un-priceable in this currency" (§4) and must never be masked by the
 *  native figure, which is in a DIFFERENT currency under the new contract. */
export function planNightly(p: RatePlan): number | null {
  return p.resNightly === undefined ? p.nightly : p.resNightly;
}
/** Per-room STAY amount of one rate plan, in the reservation currency, for `n` nights (WI 3976):
 *  the BE's own reservationTotal, never per-night × nights — the nightly is a display figure and
 *  multiplying it back drifts from what the wallet is charged. Three-state like planNightly():
 *  null = un-priceable (§4). A plan held from before resStayTotal existed falls back to the only
 *  figures it carries: the native stay total for a pre-contract plan, else per-night × nights. */
export function planStayTotal(p: RatePlan, n: number): number | null {
  if (p.resStayTotal !== undefined) return p.resStayTotal;
  if (p.resNightly === undefined && p.stayTotal !== undefined) return p.stayTotal;
  const v = planNightly(p);
  return v == null ? null : exactMoney(v * n);
}
/** Both totals of a booking line of `rooms` rooms (WI 3976): the BE per-room stay totals × rooms,
 *  never a per-night figure × nights. `total` is the native one (never rendered, decision A),
 *  `resTotal` the reservation-currency one every surface renders.
 *  How close resTotal is to the debit: the search converts each room's total and rounds it to 2
 *  decimals at the search-time rate, so this is round2(per-room × rate) × rooms; the BE converts
 *  the native per-room total × rooms once, at a fresh rate, and rounds that sum. Single-room and
 *  same-currency bookings match exactly; a multi-room cross-currency booking can still differ by
 *  up to 0.005 × rooms, plus whatever the rate moved between search and booking. */
export function lineTotalsOf(p: RatePlan, n: number, rooms: number): { total: number; resTotal: number | null } {
  const stay = planStayTotal(p, n);
  return {
    total: exactMoney((p.stayTotal ?? p.nightly * n) * rooms),
    resTotal: stay == null ? null : exactMoney(stay * rooms),
  };
}
/** Cheapest per-night price across a hotel's plans, in the reservation currency. `null` when NO
 *  plan can be priced in it (§4 "FX unavailable") — the hotel still renders, flagged, and sorts
 *  last; it is never dropped. Plans that are individually un-priceable are skipped as long as at
 *  least one priced plan exists. */
export function lowestNightly(h: HotelOffer): number | null {
  const priced = h.rooms.flatMap(r => r.plans.map(planNightly)).filter((v): v is number => v != null);
  return priced.length ? Math.min(...priced) : null;
}
/** Cheapest per-room STAY amount across a hotel's plans for `n` nights, in the reservation
 *  currency (WI 3976). null under the same rule as lowestNightly(). */
export function lowestStayTotal(h: HotelOffer, n: number): number | null {
  const priced = h.rooms.flatMap(r => r.plans.map(p => planStayTotal(p, n))).filter((v): v is number => v != null);
  return priced.length ? Math.min(...priced) : null;
}
/** A hotel's "from" total for `n` nights and `rooms` rooms, in the reservation currency — the
 *  single figure both the result card and the map pin show (WI 3976): the cheapest plan's BE
 *  per-room stay total × rooms, i.e. the line total checkout would show for that plan (see
 *  lineTotalsOf()). null = no plan can be priced in the selected currency (§4). */
export function fromTotal(h: HotelOffer, n: number, rooms: number): number | null {
  const stay = lowestStayTotal(h, n);
  return stay == null ? null : exactMoney(stay * rooms);
}
/** Price-sort comparator over converted amounts — un-priceable offers (null) always sort last,
 *  regardless of direction, so a missing rate can never masquerade as "cheapest". */
export function comparePriceNullsLast(a: number | null, b: number | null): number {
  if (a == null) return b == null ? 0 : 1;
  if (b == null) return -1;
  return a - b;
}
/** One booking line's total in the reservation currency — the only line amount ever rendered
 *  (decision A). Same three-state contract as planNightly(): `undefined` resTotal means the line
 *  predates the reservation-currency contract, so the native `total` (already converted by the
 *  old BE) stands in; an explicit `null` stays null — genuinely un-priceable (§4). */
export function lineResTotal(l: SelectedRate): number | null {
  return l.resTotal === undefined ? l.total : l.resTotal;
}
/** Booking-wide total in the reservation currency. `null` as soon as ONE line cannot be priced
 *  in it: the wallet is debited once, for the whole booking, so a partially-priceable selection
 *  is not bookable at all (§4 — offer detail and checkout both block on this, they never charge
 *  a partial sum). An empty selection totals 0, not null — nothing is blocked yet. */
export function resGrandTotal(lines: SelectedRate[]): number | null {
  let sum = 0;
  for (const l of lines) {
    const t = lineResTotal(l);
    if (t == null) return null;
    sum += t;
  }
  return exactMoney(sum);
}
/** Hotel-card badge sub-label source (feature: hotel-b2b-source-selection, US-3) — a merged hotel
 *  can in principle hold Extranet plans of both scopes, but the badge shows one hotel-level value
 *  (same "primary, not exhaustive" tradeoff HotelOffer.source itself already makes for AMADEUS vs
 *  EXTRANET, see mapAmadeusHotel()'s 'MIXED' comment in hotel-sourcing.service.ts): the first
 *  Extranet plan found is representative. undefined for an all-Amadeus hotel or one with no
 *  scope info yet. */
export function hotelExtranetScope(h: HotelOffer): 'MARKETPLACE' | 'PRIVATE' | undefined {
  for (const room of h.rooms) {
    for (const plan of room.plans) {
      if (plan.extranetScope) return plan.extranetScope;
    }
  }
  return undefined;
}
/** Maps a single RatePlan to its granular SearchSource id (feature: hotel-b2b-source-selection
 *  chip-UI redesign, design_handoff_hotel_search_sources) — unlike hotelExtranetScope() above
 *  (one representative value per HOTEL), the filters rail's per-source result counts and
 *  hideSources narrowing need PER-PLAN granularity, since one merged hotel can hold plans from
 *  more than one source. null for BEDBANK or an Extranet plan whose scope isn't set — never
 *  hideable, the safe default is to always show what can't be classified. */
export function planSourceId(p: RatePlan): SearchSource | null {
  if (p.source === 'AMADEUS') return 'AMADEUS';
  if (p.source === 'EXTRANET') {
    if (p.extranetScope === 'PRIVATE') return 'EXTRANET_ALLOTMENT';
    if (p.extranetScope === 'MARKETPLACE') return 'EXTRANET_HOTEL';
  }
  return null;
}
export function haversineKm(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

/** Deep-enough copy of a search criteria object — `childAges` is copied, not aliased, so editing
 *  one copy's per-child ages never mutates another (e.g. offer-detail's `crit` vs `editCrit`). */
export function cloneCrit(c: HotelSearchCriteria): HotelSearchCriteria {
  return { ...c, childAges: [...(c.childAges ?? [])] };
}
/** adults / children (excl. infants) / infants (age < 2), for the "X adults · Y children · Z
 *  infants" guest summary. */
export function splitGuests(c: HotelSearchCriteria): { adults: number; children: number; infants: number } {
  const ages = c.childAges ?? [];
  const infants = ages.filter(a => a < 2).length;
  return { adults: c.adults, children: (c.children ?? 0) - infants, infants };
}
/** Neither the Extranet nor the Amadeus search endpoints accept a "children" field (see
 *  hotel-sourcing.service.ts) — non-infant children occupy a bed like an adult, so they're summed
 *  into `adults` for the actual search request. Infants (age < 2) don't occupy a bed and are
 *  excluded entirely. The original adults/children/childAges breakdown is only for FE display. */
export function toSearchPayload(c: HotelSearchCriteria): HotelSearchCriteria {
  const extra = (c.childAges ?? []).filter(a => a >= 2).length;
  return { ...c, adults: c.adults + extra };
}

/* ----------------------------- Reference data ----------------------------- */
// INTERIM (MVP): static list aligned to the Extranet's seeded marketplace cities so search
// returns live results. Phase 2 replaces this with the Amadeus locations/autocomplete API.
export const DESTINATIONS = [
  { code: 'ROM', city: 'Roma', country: 'IT' }, { code: 'VCE', city: 'Venezia', country: 'IT' },
  { code: 'CMO', city: 'Como', country: 'IT' }, { code: 'CTA', city: 'Taormina', country: 'IT' },
  { code: 'SAY', city: 'Siena', country: 'IT' }, { code: 'SOR', city: 'Sorrento', country: 'IT' },
  { code: 'ALB', city: 'Alberobello', country: 'IT' }, { code: 'RAV', city: 'Ravello', country: 'IT' },
  { code: 'CRT', city: "Cortina d'Ampezzo", country: 'IT' }, { code: 'ORT', city: 'Ortisei', country: 'IT' },
  { code: 'JED', city: 'Jeddah', country: 'SA' }, { code: 'MAK', city: 'Makkah', country: 'SA' },
];

export const AMENITY_LABELS: Record<string, string> = {
  /* Canonical FE codes */
  WIFI: 'Wi-Fi', POOL: 'Pool', SPA: 'Spa', GYM: 'Gym', PARKING: 'Parking', RESTAURANT: 'Restaurant',
  BAR: 'Bar', BREAKFAST: 'Breakfast included', ROOM_SERVICE: 'Room service', CONCIERGE: 'Concierge',
  AC: 'Air conditioning', TERRACE: 'Terrace', GARDEN: 'Garden', SEA_VIEW: 'Sea view',
  LAKE_VIEW: 'Lake view', CANAL_VIEW: 'Canal view', MOUNTAIN_VIEW: 'Mountain view',
  // ExtranetRoomType.view's ViewType enum (SEA_VIEW/MOUNTAIN_VIEW above already cover 2 of its 7
  // values) — folded into a room's highlights chips, see groupOffersIntoRooms/mapExtranetHotel.
  CITY_VIEW: 'City view', GARDEN_VIEW: 'Garden view', LANDMARK_VIEW: 'Landmark view',
  HARAM_VIEW: 'Haram view', POOL_VIEW: 'Pool view',
  HISTORIC_BUILDING: 'Historic building', SKI_IN_SKI_OUT: 'Ski-in/ski-out', SKI_STORAGE: 'Ski storage',
  SAUNA: 'Sauna', FIREPLACE: 'Fireplace', CYCLING: 'Cycling', PET_FRIENDLY: 'Pet friendly',
  MEETING_ROOMS: 'Meeting rooms', AIRPORT_SHUTTLE: 'Airport shuttle', VINEYARD: 'Vineyard',
  BEACH_ACCESS: 'Beach access', ALL_INCLUSIVE: 'All inclusive',
  /* BE Extranet codes (snake_case variants) */
  FREE_WIFI: 'Wi-Fi', AIR_CONDITIONING: 'Air conditioning', LAKE_VIEW2: 'Lake view',
  BEACH_FRONT: 'Beach front', KIDS_CLUB: 'Kids club', LAUNDRY: 'Laundry',
  NON_SMOKING: 'Non-smoking rooms', NONSMOKING: 'Non-smoking rooms', ACCESSIBLE: 'Accessible',
  AIRCON: 'Air conditioning', PETS: 'Pet friendly', SHUTTLE: 'Airport shuttle',
  ROOMSERVICE: 'Room service', MEETING: 'Meeting rooms', KIDS: 'Kids club', EVCHARGE: 'EV charging',
  /* Amadeus connector codes (309, delivered by Claude Design as amenity-icons.js -- see
   * claude-aI-agents/claude-design/Design system e prototype B2B hotel-out/amenity-icons.js) */
  '220V_AC_ELECTRICAL_SUPPLY': '220V AC supply',
  '24_HOUR_ROOM_SERVICE': '24-hour room service',
  'ACCESSIBLE_BATHING_CONTROLS': 'Accessible bathing controls',
  'ACCESSIBLE_BATHS': 'Accessible baths',
  'ACCESSIBLE_ELEVATORS': 'Accessible elevators',
  'ACCESSIBLE_FACILITIES': 'Accessible facilities',
  'ACCESSIBLE_LIGHT_SWITCH': 'Accessible light switch',
  'ACCESSIBLE_PARKING': 'Accessible parking',
  'ACCESSIBLE_ROOM': 'Accessible room',
  'ACCESSIBLE_TOILETS': 'Accessible toilets',
  'ACCESSIBLE_WASH_BASINS': 'Accessible wash basins',
  'ADAPTED_BATHROOMS': 'Adapted bathrooms',
  'ADAPTED_ROOM_DOORS': 'Adapted room doors',
  'AIR_CONDITIONING_INDIVIDUALLY_CONTROLLED_IN_ROOM': 'In-room air conditioning',
  'AIR_LINE_DESK': 'Airline desk',
  'ALARMS_CONTINUOUSLY_MONITORED': 'Monitored alarms',
  'ALARMS_HAVE_STROBE_LIGHTS_IN_HALLWAYS': 'Strobe alarms (halls)',
  'ALARMS_HAVE_STROBE_LIGHTS_IN_PUBLIC_AREA': 'Strobe alarms (public)',
  'ALARM_CLOCK': 'Alarm clock',
  'ALL_NEWS_CHANNEL': 'All-news channel',
  'AM_OR_FM_RADIO': 'AM / FM radio',
  'ANALOG_DIALUP_CONNECTIVITY': 'Analog dial-up',
  'ANALOG_DIALUP_DATAPORT': 'Analog dataport',
  'ATM_OR_CASH_MACHINE': 'ATM / cash machine',
  'AUDIBLE_ALARMS': 'Audible alarms',
  'AUDIBLE_SMOKE_ALARMS_HARDWIRED': 'Hardwired smoke alarms',
  'AUDIBLE_SMOKE_ALARMS_IN_HALLS': 'Smoke alarms in halls',
  'AUDIBLE_SMOKE_ALARMS_IN_PUBLIC_AREAS': 'Smoke alarms (public)',
  'AUDIBLE_SMOKE_ALARMS_IN_ROOMS': 'Smoke alarms in rooms',
  'AUDIO_VISUAL_EQUIPMENT_AVAILABLE': 'Audio-visual equipment',
  'AUTOLINK_TO_FIRE_DEPARTMENT': 'Auto-link to fire dept.',
  'AUTOMATED_EXTERNAL_DEFIBRILLATOR_ON_SITE_AED': 'AED on site',
  'AUTOMATIC_FIRE_DOORS': 'Automatic fire doors',
  'AUTO_RECALL_ELEVATORS': 'Auto-recall elevators',
  'BABY_SITTING': 'Babysitting',
  'BARBECUE': 'Barbecue',
  'BASIC_MEDICAL_EQUIPMENT_ON_SITE': 'Basic medical equipment',
  'BATHROBE': 'Bathrobe',
  'BATHTUB_OR_SHOWER_COMBINATION': 'Bathtub / shower combo',
  'BEACH_NEAR_HOTEL': 'Beach nearby',
  'BEAUTY_PARLOUR': 'Beauty parlour',
  'BEDROOM_WHEELCHAIR_ACCESS': 'Wheelchair-accessible bedroom',
  'BIDET': 'Bidet',
  'BOATING': 'Boating',
  'BOTTLED_WATER': 'Bottled water',
  'BREAKFAST_FULL': 'Full breakfast',
  'BREAKFAST_SERVED_IN_RESTAURANT': 'Breakfast in restaurant',
  'BUFFET_BREAKFAST': 'Buffet breakfast',
  'BUSINESSFACILITIES_CENTRE': 'Business centre',
  'BUSINESS_CENTER': 'Business center',
  'BUSINESS_SERVICES': 'Business services',
  'CABLE_TELEVISION': 'Cable television',
  'CARDIOVASCULAREQUIPMENT': 'Cardio equipment',
  'CAR_RENTAL': 'Car rental',
  'CATERING_SERVICES': 'Catering services',
  'CHAIR_PROVIDED_WITH_DESK': 'Desk chair',
  'CHILDRENS_PROGRAM': 'Children\'s program',
  'CHILDREN_WELCOME': 'Children welcome',
  'CHILD_PROGRAMS': 'Child programs',
  'CNN_AVAILABLE': 'CNN available',
  'COACH_OR_BUS_PARKING': 'Coach / bus parking',
  'COCKTAIL_LOUNGE_WITH_ENTERTAINMENT': 'Cocktail lounge (entertainment)',
  'COCKTAIL_LOUNGE_WITH_LIGHT_FARE': 'Cocktail lounge (light fare)',
  'COFFEE_SHOP': 'Coffee shop',
  'COMPLIMENARY_NEWSPAPER_IN_LOBBY': 'Newspaper in lobby',
  'COMPLIMENTARY_FULL_AMERICAN_BREAKFAST': 'Full American breakfast',
  'COMPLIMENTARY_IN_ROOM_COFFEE_OR_TEA': 'In-room coffee / tea',
  'COMPUTER': 'Computer',
  'COMPUTER_RENTAL': 'Computer rental',
  'CONFERENCE_FACILITIES': 'Conference facilities',
  'CONFERENCE_SUITE': 'Conference suite',
  'CONNECTING_ROOMS': 'Connecting rooms',
  'CONTINENTAL_BREAKFAST': 'Continental breakfast',
  'CONVENTION_CENTRE': 'Convention centre',
  'CONVERTERS_OR_VOLTAGE_ADAPTORS': 'Voltage adaptors',
  'COPIER': 'Copier',
  'CRIBS_AVAILABLE_IF_REQUIRED': 'Cribs available',
  'CURRENCY_EXCHANGE_FACILITIES': 'Currency exchange',
  'DEADBOLTS_ON_ROOM_DOORS': 'Deadbolts on doors',
  'DESK_WITH_ELECTRICAL_OUTLET': 'Desk with power outlet',
  'DINNER_SERVED_IN_RESTAURANT': 'Dinner in restaurant',
  'DIRECT_DIAL_PHONE': 'Direct-dial phone',
  'DIRECT_DIAL_TELEPHONE': 'Direct-dial telephone',
  'DISABLED_FACILITIES': 'Disabled facilities',
  'DISABLED_PARKING': 'Disabled parking',
  'DOCTOR_ON_CALL': 'Doctor on call',
  'DOORS_HAVE_VIEWPORTS': 'Door viewports',
  'DOUBLE_BEDS': 'Double beds',
  'DOWN_OR_FEATHER_PILLOWS': 'Feather pillows',
  'DUAL_VOLTAGE_OUTLET': 'Dual-voltage outlet',
  'DUVET': 'Duvet',
  'EARLY_CHECK_IN': 'Early check-in',
  'ELECTRICAL_CURRENT_VOLTAGE': 'Electrical voltage info',
  'ELECTRIC_GENERATOR': 'Electric generator',
  'ELECTRONIC_KEY_CARDS': 'Electronic key cards',
  'ELEVATOR': 'Elevator',
  'EMERGENCY_CORDS_OR_BUTTONS': 'Emergency cords / buttons',
  'EMERGENCY_EVACUATION_DRILL_FREQ_YEARLY': 'Yearly evacuation drill',
  'EMERGENCY_INFO_IN_ROOMS': 'Emergency info in rooms',
  'EMERGENCY_LIGHTING': 'Emergency lighting',
  'ENHANCED_SAFETY_PROTOCOL': 'Enhanced safety protocol',
  'EVACUATION_PLAN': 'Evacuation plan',
  'EVENING_RECEPTION': 'Evening reception',
  'EXECUTIVE_FLOOR': 'Executive floor',
  'EXHIBITION_OR_CONVENTION_FLOOR': 'Exhibition / convention floor',
  'EXIT_SIGNS_LIT': 'Lit exit signs',
  'EXPRESS_CHECK_IN': 'Express check-in',
  'EXPRESS_CHECK_OUT': 'Express check-out',
  'EXTERIOR_DOORS_EXCEPT_LOBBY_ENTRANCE_OF_THE_FACILITY_REQUIRE_KEY_ACCESS_AT_NIGHT_OR_AUTOMATICALLY_LOCK': 'Night-locked exterior doors',
  'FAX_FACILITIES_IN_ROOM': 'In-room fax',
  'FAX_SERVICE': 'Fax service',
  'FEATHER_BED': 'Feather bed',
  'FEMA_FIRE_SAFETYFACILITIES_COMPLIANT_IN_THE_USA_.': 'FEMA fire-safety compliant',
  'FIRE_ALARM': 'Fire alarm',
  'FIRE_ALARM_WITH_LIGHT': 'Fire alarm with light',
  'FIRE_DETECTORS': 'Fire detectors',
  'FIRE_DETECTORS_IN_HALL': 'Fire detectors in halls',
  'FIRE_DETECTORS_IN_ROOMS': 'Fire detectors in rooms',
  'FIRE_EXTINGUISHERS_IN_HALLWAYS': 'Fire extinguishers (halls)',
  'FIRE_EXTINGUISHERS_IN_PUBLIC_AREAS': 'Fire extinguishers (public)',
  'FIRE_SAFETYFACILITIES': 'Fire-safety facilities',
  'FIRE_SAFETY_COMPLIANT': 'Fire-safety compliant',
  'FIRST_AID_AVAILABLE': 'First aid available',
  'FISHING': 'Fishing',
  'FLORIST': 'Florist',
  'FREE_HIGH_SPEED_INTERNET_CONNECTION': 'Free high-speed internet',
  'FREE_HIGH_SPEED_INTERNET_IN_ROOM': 'Free in-room internet',
  'FREE_LOCAL_CALLS': 'Free local calls',
  'FREE_NEWSPAPER': 'Free newspaper',
  'FREE_PARKING': 'Free parking',
  'FRONT_DESK': 'Front desk',
  'FRONT_DESK_OPEN_24_HOURS_A_DAY': '24-hour front desk',
  'FULL_SERVICE_HOUSEKEEPING': 'Full-service housekeeping',
  'GAMES_ROOM': 'Games room',
  'GARAGE_PARKING': 'Garage parking',
  'GIFT_SHOP_OR_NEWS_STAND': 'Gift shop / news stand',
  'GOLF': 'Golf',
  'GROCERY_SHOPPING_SERVICE_AVAILABLE': 'Grocery shopping service',
  'GUARDED_CAR_PARKING': 'Guarded parking',
  'GUEST_ROOM_DOORS_HAVE_A_SECOND_LOCK': 'Second lock on doors',
  'GUEST_ROOM_DOORS_SELF_CLOSING': 'Self-closing room doors',
  'GYM_NOT_HEALTH_CLUB': 'Gym',
  'HAIR_DRESSER': 'Hairdresser',
  'HAIR_DRYER': 'Hair dryer',
  'HANDICAP_ROOM': 'Accessible room',
  'HANDRAILS_IN_BATHROOMS': 'Bathroom handrails',
  'HBO_AVAILABLE': 'HBO available',
  'HEALTH_CLUB': 'Health club',
  'HEARING_INDUCTION_LOOPS': 'Hearing induction loops',
  'HIGH_SPEED_INTERNET_ACCESS': 'High-speed internet access',
  'HIGH_SPEED_INTERNET_ACCESS_FEE': 'High-speed internet (fee)',
  'HIGH_SPEED_INTERNET_CONNECTION': 'High-speed internet',
  'HIGH_SPEED_INTERNET_IN_ROOM': 'In-room high-speed internet',
  'HIGH_SPEED_WIRELESS': 'High-speed wireless',
  'HOTEL_FOLIO_BREAKDOWN_AVAILABLE_TO_CREDIT_CARD_COMPANY': 'Folio breakdown to card co.',
  'HOTEL_SAFE_DEPOSIT_BOX_NOT_ROOM_SAFE_BOX': 'Safe deposit box',
  'HOTSPOTS': 'Wi-Fi hotspots',
  'HOURS_SECURITY': '24-hour security',
  'HOUSEKEEPING_DAILY': 'Daily housekeeping',
  'ICE_MACHINES': 'Ice machines',
  'INCOMING_FAX': 'Incoming fax',
  'INDOOR_OUTDOOR_CONNECTING_POOL': 'Indoor/outdoor pool',
  'INDOOR_SWIMMING_POOL': 'Indoor swimming pool',
  'INSTANT_HOT_WATER': 'Instant hot water',
  'INTERNATIONAL_CHANNELS': 'International channels',
  'INTERNET_SERVICES': 'Internet services',
  'IN_ROOM_INTERNET_APPLIANCE': 'In-room internet appliance',
  'IRON_AND_IRONING_BOARD': 'Iron & ironing board',
  'JACUZZI': 'Jacuzzi',
  'JOGGING_TRACK': 'Jogging track',
  'JOGGING_TRAIL': 'Jogging trail',
  'KIOSK_CONNECTIVITY': 'Kiosk connectivity',
  'KITCHEN': 'Kitchen',
  'KITCHENETTE': 'Kitchenette',
  'LARGE_DESK': 'Large desk',
  'LATE_CHECK_OUT_AVAILABLE': 'Late check-out',
  'LAUNDRY_SERVICE': 'Laundry service',
  'LCD_OR_PROJECTOR_AVAILABLE': 'LCD / projector',
  'LIGHTED_MAKEUP_MIRROR': 'Lighted makeup mirror',
  'LIMOUSINE_SERVICE': 'Limousine service',
  'LOCAL_FAXES': 'Local faxes',
  'LOUNGE': 'Lounge',
  'LUNCH_SERVED_IN_RESTAURANT': 'Lunch in restaurant',
  'MAID_SERVICE': 'Maid service',
  'MASSAGE': 'Massage',
  'MEETING_FACILITIES': 'Meeting facilities',
  'MEETS_AMERICANS_WITH_DISABILITIES_ACT_OF_1990_SPECS': 'Meets ADA 1990 specs',
  'MICROWAVE': 'Microwave',
  'MINI_BAR': 'Mini bar',
  'MINI_REFRIGERATOR': 'Mini refrigerator',
  'MOTORCYCLE_PARKING': 'Motorcycle parking',
  'MOUNTAIN_CLIMBING': 'Mountain climbing',
  'MULTIPLE_EXITS_ON_EACH_FLOOR': 'Multiple floor exits',
  'NETWORK_OR_INTERNET_PRINTING_AVAILABLE': 'Network printing',
  'NON_SMOKING_ROOMS': 'Non-smoking rooms',
  'NURSERY': 'Nursery',
  'OFFICE_RENTAL': 'Office rental',
  'OFF_SITE_PARKING': 'Off-site parking',
  'ONSITE_LAUNDRY': 'On-site laundry',
  'ON_SITE_PARKING': 'On-site parking',
  'OUTDOOR_POOL': 'Outdoor pool',
  'OUTGOING_FAX': 'Outgoing fax',
  'OUTLET_ADAPTORS': 'Outlet adaptors',
  'OVERHEAD_PROJECTOR': 'Overhead projector',
  'PARKING_AREAS_WELL_LIT': 'Well-lit parking',
  'PARKING_ATTENDANT': 'Parking attendant',
  'PARKING_FEE_MANAGED_BY_THE_HOTEL': 'Managed paid parking',
  'PARKING_LOT': 'Parking lot',
  'PAY_PER_VIEW_MOVIES_ON_TV': 'Pay-per-view movies',
  'PC_HOOK_UP_IN_ROOM': 'PC hook-up in room',
  'PETS_ALLOWED': 'Pets allowed',
  'PHONE_IN_BATHROOM': 'Phone in bathroom',
  'PHOTOCOPIER': 'Photocopier',
  'PHOTOCOPY_CENTER': 'Photocopy center',
  'PILLOW_TOP_MATTRESS': 'Pillow-top mattress',
  'POOLSIDE_SERVICE': 'Poolside service',
  'PORTER_OR_BELL_BOY': 'Porter / bellboy',
  'PREMIUM_MOVIE_CHANNELS': 'Premium movie channels',
  'PRINTER': 'Printer',
  'PRIVATE_BATHROOM': 'Private bathroom',
  'PROPERTY_MEETS_REQUIREMENTS_FOR_FIRE_SAFETY': 'Meets fire-safety req.',
  'PUBLIC_ADDRESS_SYSTEM': 'Public address system',
  'PUBLIC_AREAS_AIR_CONDITIONED': 'Air-conditioned public areas',
  'RACQUETBALLCOURT': 'Racquetball court',
  'RECREATION_SPORTS_COURT': 'Sports court',
  'REMOTE_CONTROL_TELEVISION': 'Remote-control TV',
  'RESTAURANTS': 'Restaurants',
  'RESTRICTED_ACCESS_TO_ROOM_FLOORS': 'Restricted floor access',
  'ROLLAWAY_BEDS': 'Rollaway beds',
  'ROOMS_ACCESSIBLE_FROM_THE_EXTERIOR': 'Exterior room access',
  'ROOMS_ACCESSIBLE_FROM_THE_INTERIOR': 'Interior room access',
  'ROOM_SERVICE_FULL_MENU': 'Room service (full menu)',
  'ROOM_SERVICE_LIMITED_HOURS': 'Room service (limited hours)',
  'ROOM_WINDOWS_OPEN': 'Opening windows',
  'SAFE': 'Safe',
  'SAFETY_CHAINS_ON_GUEST_ROOM_DOOR': 'Door safety chains',
  'SAFE_DEPOSIT_BOX': 'Safe deposit box',
  'SAILING': 'Sailing',
  'SCUBA_DIVING': 'Scuba diving',
  'SECRETARIAL_SERVICES': 'Secretarial services',
  'SECURED_FLOORS_AVAILABLE': 'Secured floors',
  'SECURITY': 'Security',
  'SECURITY_GUARDS': 'Security guards',
  'SECURITY_PERSONNEL_ON_SITE': 'On-site security',
  'SEPARATE_LINE_BILLING_FOR_MULTI_LINE_PHONE': 'Multi-line phone billing',
  'SEPARATE_MODEM_LINE_AVAILABLE': 'Separate modem line',
  'SEPARATE_TUB_AND_SHOWER': 'Separate tub & shower',
  'SHOE_SHINE': 'Shoe shine',
  'SHOWER': 'Shower',
  'SIGHTSEEING': 'Sightseeing',
  'SINGLE_BED': 'Single bed',
  'SLIPPERS': 'Slippers',
  'SMOKE_DETECTORS': 'Smoke detectors',
  'SMOKING': 'Smoking room',
  'SNORKELING': 'Snorkeling',
  'SOCIAL_HOUR': 'Social hour',
  'SOFA_BED': 'Sofa bed',
  'SOLARIUM': 'Solarium',
  'SPARE_ELECTRICAL_OUTLET_AVAILABLE_AT_DESK': 'Spare outlet at desk',
  'SPEAKER_PHONE': 'Speakerphone',
  'SPRINKLERS': 'Sprinklers',
  'SPRINKLERS_IN_HALL': 'Sprinklers in halls',
  'SPRINKLERS_IN_ROOMS': 'Sprinklers in rooms',
  'SQUASHCOURTS': 'Squash courts',
  'STAFF_TRAINED_FOR_DUPLICATE_KEYS': 'Duplicate-key protocol',
  'STAFF_TRAINED_IN_AUTOMATED_EXTERNAL_DEFIBRILLATOR_USAGE_AED': 'Staff trained in AED',
  'STAFF_TRAINED_IN_FIRST_AID_TECHNIQUES': 'First-aid trained staff',
  'STAFF_TRAINED_TO_CATER_FOR_DISABLED_GUESTS': 'Staff trained for disabled guests',
  'STORAGE_SPACE': 'Storage space',
  'STORAGE_SPACE_AVAILABLE': 'Storage space',
  'SWIMMING_POOL': 'Swimming pool',
  'TABLES_AND_CHAIRS': 'Tables & chairs',
  'TABLE_TENNIS': 'Table tennis',
  'TEA_AND_COFFEE_MAKING_FACILITIES': 'Tea & coffee facilities',
  'TECHNICAL_CONCIERGE_AVAILABLE': 'Technical concierge',
  'TELECONFERENCING_FACILITIES': 'Teleconferencing',
  'TELEPHONE': 'Telephone',
  'TELEPHONE_JACK_ADAPTOR_AVAILABLE': 'Telephone jack adaptor',
  'TELEVISION': 'Television',
  'TENNIS': 'Tennis',
  'TENNIS_COURT': 'Tennis court',
  'TRANSLATION_SERVICES': 'Translation services',
  'TRAVEL_AGENCY': 'Travel agency',
  'TURN_DOWN_SERVICE': 'Turndown service',
  'TWO_LINE_PHONE': 'Two-line phone',
  'TYPE_OF_ENTRANCE_TO_GUEST_ROOM': 'Guest-room entrance type',
  'UPGRADED_BATHROOM_AMENITIES': 'Upgraded bathroom amenities',
  'VALET_DRY_CLEANING': 'Valet dry cleaning',
  'VALET_PARKING': 'Valet parking',
  'VENTILATED_STAIR_WELLS': 'Ventilated stairwells',
  'VIDEO_SURVEILLANCE_IN_HALLWAYS': 'CCTV in hallways',
  'VIDEO_SURVEILLANCE_IN_PUBLIC_AREAS': 'CCTV in public areas',
  'VIDEO_SURVEILLANCE_OF_PARKING_AREAS': 'CCTV in parking',
  'VIDEO_SURVEILLANCE_RECORDED_24_HRS_A_DAY': '24-hour CCTV recording',
  'VOICE_MAIL_IN_ROOM': 'In-room voicemail',
  'WAKEUP_SERVICE': 'Wake-up service',
  'WAKE_UP_CALL_SERVICE': 'Wake-up call',
  'WEDDING_SERVICES': 'Wedding services',
  'WEIGHT_SCALE': 'Weight scale',
  'WELCOME_GIFT': 'Welcome gift',
  'WELL_LIT_WALKWAYS': 'Well-lit walkways',
  'WHEELCHAIR_ENTRANCE': 'Wheelchair entrance',
  'WHIRLPOOL': 'Whirlpool',
  'WIDE_CORRIDORS': 'Wide corridors',
  'WIDE_ENTRANCE': 'Wide entrance',
  'WIDE_RESTAURANT_ENTRANCE': 'Wide restaurant entrance',
  'WIRELESS_CONNECTIVITY': 'Wireless connectivity',
  'WIRELESS_CONNECTIVITY_IN_ROOMS': 'In-room wireless',
  'WOMEN_S_GUEST_ROOMS': 'Women\'s guest rooms',
};

/** Amenity code → readable label. Falls back to Title Case (not raw SCREAMING_SNAKE_CASE) for any
 *  code not yet in AMENITY_LABELS — e.g. Extranet room-level service codes like WARDROBE, which
 *  aren't part of the curated hotel-amenity set above. Single source used by both search results
 *  and offer detail so an unmapped code renders identically wherever it shows up. */
/** SCREAMING_SNAKE_CASE backend code → readable "Title Case" — the shared fallback for any enum
 *  code with no curated label (amenityLabel, transactionTypeLabel, margin scope/kind below), so an
 *  unmapped code reads as words instead of a raw enum wherever it surfaces in the UI. Exported
 *  (WI #1082) so hotel-sourcing.service.ts can reuse it for Amadeus's roomCategory room-name
 *  fallback ("STANDARD_ROOM" → "Standard Room") instead of a second ad-hoc formatter. */
export function titleCaseCode(code: string): string {
  return code.split('_').filter(Boolean).map(w => w[0].toUpperCase() + w.slice(1).toLowerCase()).join(' ');
}

export function amenityLabel(code: string): string {
  return AMENITY_LABELS[code] ?? titleCaseCode(code);
}

/** Wallet ledger transaction type → translated label. The wallet is shared across products
 *  (see be-agency-service), so this hotel portal's ledger can show non-hotel type codes (e.g.
 *  TICKETING from Flight Fusion) that have no i18n key here — those fall back to readable Title
 *  Case instead of the raw enum, same precedent as amenityLabel() above. `translate` is passed in
 *  by the caller (TranslateService.instant bound to the component) so this stays a plain function. */
export function transactionTypeLabel(type: string, translate: (key: string) => string): string {
  const key = type === 'HOTEL_BOOKING' || type === 'ACCOMMODATION' ? 'transactions.accommodation'
    : type === 'CHARGE' ? 'transactions.charge'
    : type === 'REFUND' ? 'transactions.refund'
    : null;
  return key ? translate(key) : titleCaseCode(type);
}

/** Transaction status → translation key, the ONE place this feature spells those four words.
 *
 *  §3 of the hotel-agency-management handoff names them: Completed, Pending, Awaiting approval,
 *  Rejected. They had drifted into three vocabularies for the same underlying states — the Wallet
 *  ledger said "Approved"/"Rejected" (WalletTopUp's own PENDING/APPROVED/REJECTED), the sub-agency
 *  ledger said "Completed"/"Failed", and the Edit Transaction dialog offered "Rejected" for the
 *  very row its own table had just called "Failed".
 *
 *  Takes the raw status in whichever spelling the caller holds, plus the charge type: a PENDING
 *  bank transfer is not merely pending, it is sitting in a head agency's approval queue, and that
 *  is the distinction an agent actually needs. Only the LABEL is unified — the status values
 *  themselves are untouched, so the chip classes each screen already sets still apply. */
export function transactionStatusKey(status: string, chargeType?: string | null): string {
  const s = (status ?? '').toUpperCase();
  if (s === 'COMPLETED' || s === 'APPROVED' || s === 'SUCCESS') return 'txStatus.completed';
  if (s === 'FAILED' || s === 'REJECTED' || s === 'CANCELED') return 'txStatus.rejected';
  return chargeType === 'BANK_TRANSFER' ? 'txStatus.awaitingApproval' : 'txStatus.pending';
}

/** Margin scope/kind → readable label. The margin table is shared across products too (flight
 *  scopes like FARE_WITH_CHECKED_BAG/ANCILLARY_SEAT can appear alongside hotel ones per the MVP
 *  note in margins.component.ts) — no curated hotel-specific wording exists yet, so this is a
 *  readability fix (Title Case instead of raw enum), not a translation. */
export function marginCodeLabel(code: string): string { return titleCaseCode(code); }

/** Amenity code → Material icon name. Falls back to `check` for unmapped codes. */
export const AMENITY_ICONS: Record<string, string> = {
  WIFI: 'wifi', FREE_WIFI: 'wifi',
  POOL: 'pool', SPA: 'spa', GYM: 'fitness_center',
  PARKING: 'local_parking', RESTAURANT: 'restaurant',
  BREAKFAST: 'free_breakfast', BAR: 'local_bar',
  AC: 'ac_unit', AIRCON: 'ac_unit', AIR_CONDITIONING: 'ac_unit',
  AIRPORT_SHUTTLE: 'airport_shuttle', SHUTTLE: 'airport_shuttle',
  CONCIERGE: 'support_agent', ROOM_SERVICE: 'room_service', ROOMSERVICE: 'room_service',
  PET_FRIENDLY: 'pets', PETS: 'pets', LAUNDRY: 'local_laundry_service',
  MEETING_ROOMS: 'meeting_room', MEETING: 'meeting_room', EVCHARGE: 'ev_station',
  GARDEN: 'yard', KIDS_CLUB: 'child_care', KIDS: 'child_care',
  NON_SMOKING: 'smoke_free', NONSMOKING: 'smoke_free', ACCESSIBLE: 'accessible',
  TERRACE: 'deck', SEA_VIEW: 'water', LAKE_VIEW: 'water', CANAL_VIEW: 'water',
  MOUNTAIN_VIEW: 'landscape', BEACH_ACCESS: 'beach_access', BEACH_FRONT: 'beach_access',
  CITY_VIEW: 'location_city', GARDEN_VIEW: 'yard', LANDMARK_VIEW: 'account_balance',
  HARAM_VIEW: 'mosque', POOL_VIEW: 'pool',
  HISTORIC_BUILDING: 'account_balance', SKI_IN_SKI_OUT: 'downhill_skiing', SKI_STORAGE: 'downhill_skiing',
  SAUNA: 'hot_tub', FIREPLACE: 'fireplace', CYCLING: 'directions_bike',
  VINEYARD: 'wine_bar', ALL_INCLUSIVE: 'all_inclusive',
  /* Amadeus connector codes -- Lucide icon names from amenity-icons.js translated to their
   * closest Material Icons equivalent (this app renders via <mat-icon>, not Lucide). */
  '220V_AC_ELECTRICAL_SUPPLY': 'bolt',
  '24_HOUR_ROOM_SERVICE': 'room_service',
  'ACCESSIBLE_BATHING_CONTROLS': 'bathtub',
  'ACCESSIBLE_BATHS': 'bathtub',
  'ACCESSIBLE_ELEVATORS': 'elevator',
  'ACCESSIBLE_FACILITIES': 'accessible',
  'ACCESSIBLE_LIGHT_SWITCH': 'lightbulb',
  'ACCESSIBLE_PARKING': 'accessible',
  'ACCESSIBLE_ROOM': 'accessible',
  'ACCESSIBLE_TOILETS': 'accessible',
  'ACCESSIBLE_WASH_BASINS': 'water_drop',
  'ADAPTED_BATHROOMS': 'bathtub',
  'ADAPTED_ROOM_DOORS': 'meeting_room',
  'AIR_CONDITIONING_INDIVIDUALLY_CONTROLLED_IN_ROOM': 'air',
  'AIR_LINE_DESK': 'flight',
  'ALARMS_CONTINUOUSLY_MONITORED': 'emergency',
  'ALARMS_HAVE_STROBE_LIGHTS_IN_HALLWAYS': 'emergency',
  'ALARMS_HAVE_STROBE_LIGHTS_IN_PUBLIC_AREA': 'emergency',
  'ALARM_CLOCK': 'alarm',
  'ALL_NEWS_CHANNEL': 'tv',
  'AM_OR_FM_RADIO': 'radio',
  'ANALOG_DIALUP_CONNECTIVITY': 'cable',
  'ANALOG_DIALUP_DATAPORT': 'cable',
  'ATM_OR_CASH_MACHINE': 'payments',
  'AUDIBLE_ALARMS': 'notifications_active',
  'AUDIBLE_SMOKE_ALARMS_HARDWIRED': 'emergency',
  'AUDIBLE_SMOKE_ALARMS_IN_HALLS': 'emergency',
  'AUDIBLE_SMOKE_ALARMS_IN_PUBLIC_AREAS': 'emergency',
  'AUDIBLE_SMOKE_ALARMS_IN_ROOMS': 'emergency',
  'AUDIO_VISUAL_EQUIPMENT_AVAILABLE': 'cast',
  'AUTOLINK_TO_FIRE_DEPARTMENT': 'local_fire_department',
  'AUTOMATED_EXTERNAL_DEFIBRILLATOR_ON_SITE_AED': 'monitor_heart',
  'AUTOMATIC_FIRE_DOORS': 'sensor_door',
  'AUTO_RECALL_ELEVATORS': 'elevator',
  'BABY_SITTING': 'child_friendly',
  'BARBECUE': 'local_fire_department',
  'BASIC_MEDICAL_EQUIPMENT_ON_SITE': 'medical_services',
  'BATHROBE': 'checkroom',
  'BATHTUB_OR_SHOWER_COMBINATION': 'bathtub',
  'BEACH_NEAR_HOTEL': 'beach_access',
  'BEAUTY_PARLOUR': 'auto_awesome',
  'BEDROOM_WHEELCHAIR_ACCESS': 'accessible',
  'BIDET': 'water_drop',
  'BOATING': 'sailing',
  'BOTTLED_WATER': 'local_drink',
  'BREAKFAST_FULL': 'bakery_dining',
  'BREAKFAST_SERVED_IN_RESTAURANT': 'local_cafe',
  'BUFFET_BREAKFAST': 'restaurant',
  'BUSINESSFACILITIES_CENTRE': 'work',
  'BUSINESS_CENTER': 'work',
  'BUSINESS_SERVICES': 'work',
  'CABLE_TELEVISION': 'tv',
  'CARDIOVASCULAREQUIPMENT': 'monitor_heart',
  'CAR_RENTAL': 'directions_car',
  'CATERING_SERVICES': 'restaurant_menu',
  'CHAIR_PROVIDED_WITH_DESK': 'chair',
  'CHILDRENS_PROGRAM': 'child_friendly',
  'CHILDREN_WELCOME': 'child_friendly',
  'CHILD_PROGRAMS': 'child_friendly',
  'CNN_AVAILABLE': 'tv',
  'COACH_OR_BUS_PARKING': 'directions_bus',
  'COCKTAIL_LOUNGE_WITH_ENTERTAINMENT': 'local_bar',
  'COCKTAIL_LOUNGE_WITH_LIGHT_FARE': 'local_bar',
  'COFFEE_SHOP': 'local_cafe',
  'COMPLIMENARY_NEWSPAPER_IN_LOBBY': 'article',
  'COMPLIMENTARY_FULL_AMERICAN_BREAKFAST': 'egg_alt',
  'COMPLIMENTARY_IN_ROOM_COFFEE_OR_TEA': 'local_cafe',
  'COMPUTER': 'computer',
  'COMPUTER_RENTAL': 'computer',
  'CONFERENCE_FACILITIES': 'co_present',
  'CONFERENCE_SUITE': 'co_present',
  'CONNECTING_ROOMS': 'meeting_room',
  'CONTINENTAL_BREAKFAST': 'bakery_dining',
  'CONVENTION_CENTRE': 'co_present',
  'CONVERTERS_OR_VOLTAGE_ADAPTORS': 'power',
  'COPIER': 'content_copy',
  'CRIBS_AVAILABLE_IF_REQUIRED': 'child_friendly',
  'CURRENCY_EXCHANGE_FACILITIES': 'payments',
  'DEADBOLTS_ON_ROOM_DOORS': 'lock',
  'DESK_WITH_ELECTRICAL_OUTLET': 'power',
  'DINNER_SERVED_IN_RESTAURANT': 'restaurant',
  'DIRECT_DIAL_PHONE': 'phone',
  'DIRECT_DIAL_TELEPHONE': 'phone',
  'DISABLED_FACILITIES': 'accessible',
  'DISABLED_PARKING': 'accessible',
  'DOCTOR_ON_CALL': 'medical_services',
  'DOORS_HAVE_VIEWPORTS': 'visibility',
  'DOUBLE_BEDS': 'king_bed',
  'DOWN_OR_FEATHER_PILLOWS': 'bed',
  'DUAL_VOLTAGE_OUTLET': 'power',
  'DUVET': 'bed',
  'EARLY_CHECK_IN': 'login',
  'ELECTRICAL_CURRENT_VOLTAGE': 'bolt',
  'ELECTRIC_GENERATOR': 'bolt',
  'ELECTRONIC_KEY_CARDS': 'vpn_key',
  'ELEVATOR': 'elevator',
  'EMERGENCY_CORDS_OR_BUTTONS': 'notifications_active',
  'EMERGENCY_EVACUATION_DRILL_FREQ_YEARLY': 'map',
  'EMERGENCY_INFO_IN_ROOMS': 'info',
  'EMERGENCY_LIGHTING': 'lightbulb',
  'ENHANCED_SAFETY_PROTOCOL': 'verified_user',
  'EVACUATION_PLAN': 'map',
  'EVENING_RECEPTION': 'wine_bar',
  'EXECUTIVE_FLOOR': 'apartment',
  'EXHIBITION_OR_CONVENTION_FLOOR': 'co_present',
  'EXIT_SIGNS_LIT': 'meeting_room',
  'EXPRESS_CHECK_IN': 'login',
  'EXPRESS_CHECK_OUT': 'logout',
  'EXTERIOR_DOORS_EXCEPT_LOBBY_ENTRANCE_OF_THE_FACILITY_REQUIRE_KEY_ACCESS_AT_NIGHT_OR_AUTOMATICALLY_LOCK': 'lock',
  'FAX_FACILITIES_IN_ROOM': 'print',
  'FAX_SERVICE': 'print',
  'FEATHER_BED': 'bed',
  'FEMA_FIRE_SAFETYFACILITIES_COMPLIANT_IN_THE_USA_.': 'verified_user',
  'FIRE_ALARM': 'emergency',
  'FIRE_ALARM_WITH_LIGHT': 'emergency',
  'FIRE_DETECTORS': 'local_fire_department',
  'FIRE_DETECTORS_IN_HALL': 'local_fire_department',
  'FIRE_DETECTORS_IN_ROOMS': 'local_fire_department',
  'FIRE_EXTINGUISHERS_IN_HALLWAYS': 'fire_extinguisher',
  'FIRE_EXTINGUISHERS_IN_PUBLIC_AREAS': 'fire_extinguisher',
  'FIRE_SAFETYFACILITIES': 'local_fire_department',
  'FIRE_SAFETY_COMPLIANT': 'local_fire_department',
  'FIRST_AID_AVAILABLE': 'monitor_heart',
  'FISHING': 'phishing',
  'FLORIST': 'local_florist',
  'FREE_HIGH_SPEED_INTERNET_CONNECTION': 'wifi',
  'FREE_HIGH_SPEED_INTERNET_IN_ROOM': 'wifi',
  'FREE_LOCAL_CALLS': 'call',
  'FREE_NEWSPAPER': 'article',
  'FREE_PARKING': 'local_parking',
  'FRONT_DESK': 'room_service',
  'FRONT_DESK_OPEN_24_HOURS_A_DAY': 'schedule',
  'FULL_SERVICE_HOUSEKEEPING': 'auto_awesome',
  'GAMES_ROOM': 'sports_esports',
  'GARAGE_PARKING': 'local_parking',
  'GIFT_SHOP_OR_NEWS_STAND': 'card_giftcard',
  'GOLF': 'flag',
  'GROCERY_SHOPPING_SERVICE_AVAILABLE': 'shopping_cart',
  'GUARDED_CAR_PARKING': 'shield',
  'GUEST_ROOM_DOORS_HAVE_A_SECOND_LOCK': 'lock',
  'GUEST_ROOM_DOORS_SELF_CLOSING': 'sensor_door',
  'GYM_NOT_HEALTH_CLUB': 'fitness_center',
  'HAIR_DRESSER': 'content_cut',
  'HAIR_DRYER': 'air',
  'HANDICAP_ROOM': 'accessible',
  'HANDRAILS_IN_BATHROOMS': 'accessible',
  'HBO_AVAILABLE': 'tv',
  'HEALTH_CLUB': 'fitness_center',
  'HEARING_INDUCTION_LOOPS': 'hearing',
  'HIGH_SPEED_INTERNET_ACCESS': 'wifi',
  'HIGH_SPEED_INTERNET_ACCESS_FEE': 'wifi',
  'HIGH_SPEED_INTERNET_CONNECTION': 'wifi',
  'HIGH_SPEED_INTERNET_IN_ROOM': 'wifi',
  'HIGH_SPEED_WIRELESS': 'wifi',
  'HOTEL_FOLIO_BREAKDOWN_AVAILABLE_TO_CREDIT_CARD_COMPANY': 'receipt',
  'HOTEL_SAFE_DEPOSIT_BOX_NOT_ROOM_SAFE_BOX': 'lock',
  'HOTSPOTS': 'wifi',
  'HOURS_SECURITY': 'verified_user',
  'HOUSEKEEPING_DAILY': 'cleaning_services',
  'ICE_MACHINES': 'ac_unit',
  'INCOMING_FAX': 'print',
  'INDOOR_OUTDOOR_CONNECTING_POOL': 'pool',
  'INDOOR_SWIMMING_POOL': 'pool',
  'INSTANT_HOT_WATER': 'water_drop',
  'INTERNATIONAL_CHANNELS': 'tv',
  'INTERNET_SERVICES': 'wifi',
  'IN_ROOM_INTERNET_APPLIANCE': 'wifi',
  'IRON_AND_IRONING_BOARD': 'checkroom',
  'JACUZZI': 'bathtub',
  'JOGGING_TRACK': 'directions_walk',
  'JOGGING_TRAIL': 'directions_walk',
  'KIOSK_CONNECTIVITY': 'computer',
  'KITCHEN': 'kitchen',
  'KITCHENETTE': 'kitchen',
  'LARGE_DESK': 'table_restaurant',
  'LATE_CHECK_OUT_AVAILABLE': 'logout',
  'LAUNDRY_SERVICE': 'checkroom',
  'LCD_OR_PROJECTOR_AVAILABLE': 'cast',
  'LIGHTED_MAKEUP_MIRROR': 'auto_awesome',
  'LIMOUSINE_SERVICE': 'directions_car',
  'LOCAL_FAXES': 'print',
  'LOUNGE': 'weekend',
  'LUNCH_SERVED_IN_RESTAURANT': 'restaurant',
  'MAID_SERVICE': 'cleaning_services',
  'MASSAGE': 'spa',
  'MEETING_FACILITIES': 'co_present',
  'MEETS_AMERICANS_WITH_DISABILITIES_ACT_OF_1990_SPECS': 'verified_user',
  'MICROWAVE': 'microwave',
  'MINI_BAR': 'wine_bar',
  'MINI_REFRIGERATOR': 'kitchen',
  'MOTORCYCLE_PARKING': 'pedal_bike',
  'MOUNTAIN_CLIMBING': 'terrain',
  'MULTIPLE_EXITS_ON_EACH_FLOOR': 'meeting_room',
  'NETWORK_OR_INTERNET_PRINTING_AVAILABLE': 'print',
  'NON_SMOKING_ROOMS': 'smoke_free',
  'NURSERY': 'child_friendly',
  'OFFICE_RENTAL': 'work',
  'OFF_SITE_PARKING': 'local_parking',
  'ONSITE_LAUNDRY': 'local_laundry_service',
  'ON_SITE_PARKING': 'local_parking',
  'OUTDOOR_POOL': 'pool',
  'OUTGOING_FAX': 'print',
  'OUTLET_ADAPTORS': 'power',
  'OVERHEAD_PROJECTOR': 'cast',
  'PARKING_AREAS_WELL_LIT': 'lightbulb',
  'PARKING_ATTENDANT': 'how_to_reg',
  'PARKING_FEE_MANAGED_BY_THE_HOTEL': 'local_parking',
  'PARKING_LOT': 'local_parking',
  'PAY_PER_VIEW_MOVIES_ON_TV': 'movie',
  'PC_HOOK_UP_IN_ROOM': 'computer',
  'PETS_ALLOWED': 'pets',
  'PHONE_IN_BATHROOM': 'phone',
  'PHOTOCOPIER': 'content_copy',
  'PHOTOCOPY_CENTER': 'content_copy',
  'PILLOW_TOP_MATTRESS': 'bed',
  'POOLSIDE_SERVICE': 'pool',
  'PORTER_OR_BELL_BOY': 'luggage',
  'PREMIUM_MOVIE_CHANNELS': 'movie',
  'PRINTER': 'print',
  'PRIVATE_BATHROOM': 'bathtub',
  'PROPERTY_MEETS_REQUIREMENTS_FOR_FIRE_SAFETY': 'verified_user',
  'PUBLIC_ADDRESS_SYSTEM': 'campaign',
  'PUBLIC_AREAS_AIR_CONDITIONED': 'air',
  'RACQUETBALLCOURT': 'sports',
  'RECREATION_SPORTS_COURT': 'sports',
  'REMOTE_CONTROL_TELEVISION': 'tv',
  'RESTAURANTS': 'restaurant',
  'RESTRICTED_ACCESS_TO_ROOM_FLOORS': 'lock',
  'ROLLAWAY_BEDS': 'bed',
  'ROOMS_ACCESSIBLE_FROM_THE_EXTERIOR': 'meeting_room',
  'ROOMS_ACCESSIBLE_FROM_THE_INTERIOR': 'sensor_door',
  'ROOM_SERVICE_FULL_MENU': 'room_service',
  'ROOM_SERVICE_LIMITED_HOURS': 'room_service',
  'ROOM_WINDOWS_OPEN': 'air',
  'SAFE': 'lock',
  'SAFETY_CHAINS_ON_GUEST_ROOM_DOOR': 'lock',
  'SAFE_DEPOSIT_BOX': 'lock',
  'SAILING': 'sailing',
  'SCUBA_DIVING': 'pool',
  'SECRETARIAL_SERVICES': 'assignment',
  'SECURED_FLOORS_AVAILABLE': 'lock',
  'SECURITY': 'shield',
  'SECURITY_GUARDS': 'shield',
  'SECURITY_PERSONNEL_ON_SITE': 'shield',
  'SEPARATE_LINE_BILLING_FOR_MULTI_LINE_PHONE': 'phone',
  'SEPARATE_MODEM_LINE_AVAILABLE': 'wifi',
  'SEPARATE_TUB_AND_SHOWER': 'shower',
  'SHOE_SHINE': 'directions_walk',
  'SHOWER': 'shower',
  'SIGHTSEEING': 'photo_camera',
  'SINGLE_BED': 'single_bed',
  'SLIPPERS': 'directions_walk',
  'SMOKE_DETECTORS': 'emergency',
  'SMOKING': 'smoking_rooms',
  'SNORKELING': 'pool',
  'SOCIAL_HOUR': 'wine_bar',
  'SOFA_BED': 'weekend',
  'SOLARIUM': 'wb_sunny',
  'SPARE_ELECTRICAL_OUTLET_AVAILABLE_AT_DESK': 'power',
  'SPEAKER_PHONE': 'phone',
  'SPRINKLERS': 'water_drop',
  'SPRINKLERS_IN_HALL': 'water_drop',
  'SPRINKLERS_IN_ROOMS': 'water_drop',
  'SQUASHCOURTS': 'sports',
  'STAFF_TRAINED_FOR_DUPLICATE_KEYS': 'vpn_key',
  'STAFF_TRAINED_IN_AUTOMATED_EXTERNAL_DEFIBRILLATOR_USAGE_AED': 'monitor_heart',
  'STAFF_TRAINED_IN_FIRST_AID_TECHNIQUES': 'monitor_heart',
  'STAFF_TRAINED_TO_CATER_FOR_DISABLED_GUESTS': 'accessible',
  'STORAGE_SPACE': 'inventory_2',
  'STORAGE_SPACE_AVAILABLE': 'inventory_2',
  'SWIMMING_POOL': 'pool',
  'TABLES_AND_CHAIRS': 'chair',
  'TABLE_TENNIS': 'sports',
  'TEA_AND_COFFEE_MAKING_FACILITIES': 'local_cafe',
  'TECHNICAL_CONCIERGE_AVAILABLE': 'headset_mic',
  'TELECONFERENCING_FACILITIES': 'videocam',
  'TELEPHONE': 'phone',
  'TELEPHONE_JACK_ADAPTOR_AVAILABLE': 'power',
  'TELEVISION': 'tv',
  'TENNIS': 'sports',
  'TENNIS_COURT': 'sports',
  'TRANSLATION_SERVICES': 'translate',
  'TRAVEL_AGENCY': 'flight',
  'TURN_DOWN_SERVICE': 'bed',
  'TWO_LINE_PHONE': 'phone',
  'TYPE_OF_ENTRANCE_TO_GUEST_ROOM': 'meeting_room',
  'UPGRADED_BATHROOM_AMENITIES': 'bathtub',
  'VALET_DRY_CLEANING': 'checkroom',
  'VALET_PARKING': 'directions_car',
  'VENTILATED_STAIR_WELLS': 'air',
  'VIDEO_SURVEILLANCE_IN_HALLWAYS': 'videocam',
  'VIDEO_SURVEILLANCE_IN_PUBLIC_AREAS': 'videocam',
  'VIDEO_SURVEILLANCE_OF_PARKING_AREAS': 'videocam',
  'VIDEO_SURVEILLANCE_RECORDED_24_HRS_A_DAY': 'videocam',
  'VOICE_MAIL_IN_ROOM': 'voicemail',
  'WAKEUP_SERVICE': 'alarm',
  'WAKE_UP_CALL_SERVICE': 'alarm',
  'WEDDING_SERVICES': 'favorite',
  'WEIGHT_SCALE': 'monitor_weight',
  'WELCOME_GIFT': 'card_giftcard',
  'WELL_LIT_WALKWAYS': 'lightbulb',
  'WHEELCHAIR_ENTRANCE': 'accessible',
  'WHIRLPOOL': 'bathtub',
  'WIDE_CORRIDORS': 'straighten',
  'WIDE_ENTRANCE': 'straighten',
  'WIDE_RESTAURANT_ENTRANCE': 'straighten',
  'WIRELESS_CONNECTIVITY': 'wifi',
  'WIRELESS_CONNECTIVITY_IN_ROOMS': 'wifi',
  'WOMEN_S_GUEST_ROOMS': 'person',
};
export function amenityIcon(code: string): string { return AMENITY_ICONS[code] ?? 'check'; }

export interface AmenityCategory { key: string; label: string; icon: string; }
/** Ordered amenity categories — drives the grouped amenities preview + slide-over on the offer page. */
export const AMENITY_CATS: AmenityCategory[] = [
  { key: 'general', label: 'General', icon: 'hotel' },
  { key: 'wellness', label: 'Wellness & Leisure', icon: 'spa' },
  { key: 'food', label: 'Food & Drink', icon: 'restaurant' },
  { key: 'services', label: 'Services', icon: 'concierge' },
  { key: 'room', label: 'Room Facilities', icon: 'bed' },
  { key: 'business', label: 'Business', icon: 'business_center' },
  { key: 'accessibility', label: 'Accessibility', icon: 'accessible' },
  { key: 'safety', label: 'Safety & Security', icon: 'shield' },
];

/** Amenity code → category key. Covers every key in AMENITY_LABELS; falls back to 'general'. */
export const AMENITY_CAT: Record<string, string> = {
  /* General */
  'ALL_INCLUSIVE': 'general', 'ANALOG_DIALUP_CONNECTIVITY': 'general', 'BEACH_ACCESS': 'general',
  'BEACH_FRONT': 'general', 'BEACH_NEAR_HOTEL': 'general', 'CHILDRENS_PROGRAM': 'general',
  'CHILDREN_WELCOME': 'general', 'CHILD_PROGRAMS': 'general', 'CRIBS_AVAILABLE_IF_REQUIRED': 'general',
  'ELEVATOR': 'general', 'EVCHARGE': 'general', 'EVENING_RECEPTION': 'general',
  'FREE_HIGH_SPEED_INTERNET_CONNECTION': 'general', 'FREE_WIFI': 'general', 'GARDEN': 'general',
  'HIGH_SPEED_INTERNET_ACCESS': 'general', 'HIGH_SPEED_INTERNET_ACCESS_FEE': 'general', 'HIGH_SPEED_INTERNET_CONNECTION': 'general',
  'HIGH_SPEED_WIRELESS': 'general', 'HISTORIC_BUILDING': 'general', 'INTERNET_SERVICES': 'general',
  'KIDS': 'general', 'KIDS_CLUB': 'general', 'KIOSK_CONNECTIVITY': 'general',
  'LOUNGE': 'general', 'NURSERY': 'general', 'PETS': 'general',
  'PETS_ALLOWED': 'general', 'PET_FRIENDLY': 'general', 'SIGHTSEEING': 'general',
  'SOCIAL_HOUR': 'general', 'TERRACE': 'general', 'VINEYARD': 'general',
  'WELCOME_GIFT': 'general', 'WIFI': 'general', 'WIRELESS_CONNECTIVITY': 'general',
  /* Wellness & Leisure */
  'BEAUTY_PARLOUR': 'wellness', 'BOATING': 'wellness', 'CARDIOVASCULAREQUIPMENT': 'wellness',
  'CYCLING': 'wellness', 'FISHING': 'wellness', 'GAMES_ROOM': 'wellness',
  'GOLF': 'wellness', 'GYM': 'wellness', 'GYM_NOT_HEALTH_CLUB': 'wellness',
  'HEALTH_CLUB': 'wellness', 'INDOOR_OUTDOOR_CONNECTING_POOL': 'wellness', 'INDOOR_SWIMMING_POOL': 'wellness',
  'JACUZZI': 'wellness', 'JOGGING_TRACK': 'wellness', 'JOGGING_TRAIL': 'wellness',
  'MASSAGE': 'wellness', 'MOUNTAIN_CLIMBING': 'wellness', 'OUTDOOR_POOL': 'wellness',
  'POOL': 'wellness', 'POOLSIDE_SERVICE': 'wellness', 'RACQUETBALLCOURT': 'wellness',
  'RECREATION_SPORTS_COURT': 'wellness', 'SAILING': 'wellness', 'SAUNA': 'wellness',
  'SCUBA_DIVING': 'wellness', 'SKI_IN_SKI_OUT': 'wellness', 'SKI_STORAGE': 'wellness',
  'SNORKELING': 'wellness', 'SOLARIUM': 'wellness', 'SPA': 'wellness',
  'SQUASHCOURTS': 'wellness', 'SWIMMING_POOL': 'wellness', 'TABLE_TENNIS': 'wellness',
  'TENNIS': 'wellness', 'TENNIS_COURT': 'wellness', 'WEIGHT_SCALE': 'wellness',
  'WHIRLPOOL': 'wellness',
  /* Food & Drink */
  '24_HOUR_ROOM_SERVICE': 'food', 'BAR': 'food', 'BARBECUE': 'food',
  'BREAKFAST': 'food', 'BREAKFAST_FULL': 'food', 'BREAKFAST_SERVED_IN_RESTAURANT': 'food',
  'BUFFET_BREAKFAST': 'food', 'CATERING_SERVICES': 'food', 'COCKTAIL_LOUNGE_WITH_ENTERTAINMENT': 'food',
  'COCKTAIL_LOUNGE_WITH_LIGHT_FARE': 'food', 'COFFEE_SHOP': 'food', 'COMPLIMENTARY_FULL_AMERICAN_BREAKFAST': 'food',
  'COMPLIMENTARY_IN_ROOM_COFFEE_OR_TEA': 'food', 'CONTINENTAL_BREAKFAST': 'food', 'DINNER_SERVED_IN_RESTAURANT': 'food',
  'GROCERY_SHOPPING_SERVICE_AVAILABLE': 'food', 'LUNCH_SERVED_IN_RESTAURANT': 'food', 'MINI_BAR': 'food',
  'RESTAURANT': 'food', 'RESTAURANTS': 'food', 'ROOMSERVICE': 'food',
  'ROOM_SERVICE': 'food', 'ROOM_SERVICE_FULL_MENU': 'food', 'ROOM_SERVICE_LIMITED_HOURS': 'food',
  'TEA_AND_COFFEE_MAKING_FACILITIES': 'food',
  /* Services */
  'AIRPORT_SHUTTLE': 'services', 'ATM_OR_CASH_MACHINE': 'services', 'BABY_SITTING': 'services',
  'CAR_RENTAL': 'services', 'COACH_OR_BUS_PARKING': 'services', 'COMPLIMENARY_NEWSPAPER_IN_LOBBY': 'services',
  'CONCIERGE': 'services', 'CURRENCY_EXCHANGE_FACILITIES': 'services', 'EARLY_CHECK_IN': 'services',
  'EXPRESS_CHECK_IN': 'services', 'EXPRESS_CHECK_OUT': 'services', 'FLORIST': 'services',
  'FREE_LOCAL_CALLS': 'services', 'FREE_NEWSPAPER': 'services', 'FREE_PARKING': 'services',
  'FRONT_DESK': 'services', 'FRONT_DESK_OPEN_24_HOURS_A_DAY': 'services', 'FULL_SERVICE_HOUSEKEEPING': 'services',
  'GARAGE_PARKING': 'services', 'GIFT_SHOP_OR_NEWS_STAND': 'services', 'HAIR_DRESSER': 'services',
  'HOTEL_FOLIO_BREAKDOWN_AVAILABLE_TO_CREDIT_CARD_COMPANY': 'services', 'HOUSEKEEPING_DAILY': 'services', 'LATE_CHECK_OUT_AVAILABLE': 'services',
  'LAUNDRY': 'services', 'LAUNDRY_SERVICE': 'services', 'LIMOUSINE_SERVICE': 'services',
  'LOCAL_FAXES': 'services', 'MAID_SERVICE': 'services', 'MOTORCYCLE_PARKING': 'services',
  'OFF_SITE_PARKING': 'services', 'ONSITE_LAUNDRY': 'services', 'ON_SITE_PARKING': 'services',
  'PARKING': 'services', 'PARKING_ATTENDANT': 'services', 'PARKING_FEE_MANAGED_BY_THE_HOTEL': 'services',
  'PARKING_LOT': 'services', 'PORTER_OR_BELL_BOY': 'services', 'SHOE_SHINE': 'services',
  'SHUTTLE': 'services', 'TECHNICAL_CONCIERGE_AVAILABLE': 'services', 'TRANSLATION_SERVICES': 'services',
  'TRAVEL_AGENCY': 'services', 'TURN_DOWN_SERVICE': 'services', 'VALET_DRY_CLEANING': 'services',
  'VALET_PARKING': 'services', 'WAKEUP_SERVICE': 'services', 'WAKE_UP_CALL_SERVICE': 'services',
  /* Room Facilities */
  '220V_AC_ELECTRICAL_SUPPLY': 'room', 'AC': 'room', 'AIRCON': 'room',
  'AIR_CONDITIONING': 'room', 'AIR_CONDITIONING_INDIVIDUALLY_CONTROLLED_IN_ROOM': 'room', 'ALARM_CLOCK': 'room',
  'ALL_NEWS_CHANNEL': 'room', 'AM_OR_FM_RADIO': 'room', 'ANALOG_DIALUP_DATAPORT': 'room',
  'BATHROBE': 'room', 'BATHTUB_OR_SHOWER_COMBINATION': 'room', 'BIDET': 'room',
  'BOTTLED_WATER': 'room', 'CABLE_TELEVISION': 'room', 'CANAL_VIEW': 'room',
  'CHAIR_PROVIDED_WITH_DESK': 'room', 'CNN_AVAILABLE': 'room', 'CONNECTING_ROOMS': 'room',
  'CONVERTERS_OR_VOLTAGE_ADAPTORS': 'room', 'DESK_WITH_ELECTRICAL_OUTLET': 'room', 'DIRECT_DIAL_PHONE': 'room',
  'DIRECT_DIAL_TELEPHONE': 'room', 'DOUBLE_BEDS': 'room', 'DOWN_OR_FEATHER_PILLOWS': 'room',
  'DUAL_VOLTAGE_OUTLET': 'room', 'DUVET': 'room', 'ELECTRICAL_CURRENT_VOLTAGE': 'room',
  'FEATHER_BED': 'room', 'FIREPLACE': 'room', 'FREE_HIGH_SPEED_INTERNET_IN_ROOM': 'room',
  'HAIR_DRYER': 'room', 'HBO_AVAILABLE': 'room', 'HIGH_SPEED_INTERNET_IN_ROOM': 'room',
  'HOTEL_SAFE_DEPOSIT_BOX_NOT_ROOM_SAFE_BOX': 'room', 'HOTSPOTS': 'room', 'ICE_MACHINES': 'room',
  'INSTANT_HOT_WATER': 'room', 'INTERNATIONAL_CHANNELS': 'room', 'IN_ROOM_INTERNET_APPLIANCE': 'room',
  'IRON_AND_IRONING_BOARD': 'room', 'KITCHEN': 'room', 'KITCHENETTE': 'room',
  'LAKE_VIEW': 'room', 'LAKE_VIEW2': 'room', 'LARGE_DESK': 'room',
  'LIGHTED_MAKEUP_MIRROR': 'room', 'MICROWAVE': 'room', 'MINI_REFRIGERATOR': 'room',
  'MOUNTAIN_VIEW': 'room', 'NONSMOKING': 'room', 'NON_SMOKING': 'room',
  'NON_SMOKING_ROOMS': 'room', 'OUTLET_ADAPTORS': 'room', 'PAY_PER_VIEW_MOVIES_ON_TV': 'room',
  'PC_HOOK_UP_IN_ROOM': 'room', 'PHONE_IN_BATHROOM': 'room', 'PILLOW_TOP_MATTRESS': 'room',
  'PREMIUM_MOVIE_CHANNELS': 'room', 'PRIVATE_BATHROOM': 'room', 'PUBLIC_AREAS_AIR_CONDITIONED': 'room',
  'REMOTE_CONTROL_TELEVISION': 'room', 'ROLLAWAY_BEDS': 'room', 'ROOM_WINDOWS_OPEN': 'room',
  'SAFE': 'room', 'SAFE_DEPOSIT_BOX': 'room', 'SEA_VIEW': 'room',
  'SEPARATE_LINE_BILLING_FOR_MULTI_LINE_PHONE': 'room', 'SEPARATE_MODEM_LINE_AVAILABLE': 'room', 'SEPARATE_TUB_AND_SHOWER': 'room',
  'SHOWER': 'room', 'SINGLE_BED': 'room', 'SLIPPERS': 'room',
  'SMOKING': 'room', 'SOFA_BED': 'room', 'SPARE_ELECTRICAL_OUTLET_AVAILABLE_AT_DESK': 'room',
  'SPEAKER_PHONE': 'room', 'STORAGE_SPACE': 'room', 'STORAGE_SPACE_AVAILABLE': 'room',
  'TABLES_AND_CHAIRS': 'room', 'TELEPHONE': 'room', 'TELEPHONE_JACK_ADAPTOR_AVAILABLE': 'room',
  'TELEVISION': 'room', 'TWO_LINE_PHONE': 'room', 'TYPE_OF_ENTRANCE_TO_GUEST_ROOM': 'room',
  'UPGRADED_BATHROOM_AMENITIES': 'room', 'VOICE_MAIL_IN_ROOM': 'room', 'WEDDING_SERVICES': 'room',
  'WIRELESS_CONNECTIVITY_IN_ROOMS': 'room', 'WOMEN_S_GUEST_ROOMS': 'room',
  /* Business */
  'AIR_LINE_DESK': 'business', 'AUDIO_VISUAL_EQUIPMENT_AVAILABLE': 'business', 'BUSINESSFACILITIES_CENTRE': 'business',
  'BUSINESS_CENTER': 'business', 'BUSINESS_SERVICES': 'business', 'COMPUTER': 'business',
  'COMPUTER_RENTAL': 'business', 'CONFERENCE_FACILITIES': 'business', 'CONFERENCE_SUITE': 'business',
  'CONVENTION_CENTRE': 'business', 'COPIER': 'business', 'EXECUTIVE_FLOOR': 'business',
  'EXHIBITION_OR_CONVENTION_FLOOR': 'business', 'FAX_FACILITIES_IN_ROOM': 'business', 'FAX_SERVICE': 'business',
  'INCOMING_FAX': 'business', 'LCD_OR_PROJECTOR_AVAILABLE': 'business', 'MEETING': 'business',
  'MEETING_FACILITIES': 'business', 'MEETING_ROOMS': 'business', 'NETWORK_OR_INTERNET_PRINTING_AVAILABLE': 'business',
  'OFFICE_RENTAL': 'business', 'OUTGOING_FAX': 'business', 'OVERHEAD_PROJECTOR': 'business',
  'PHOTOCOPIER': 'business', 'PHOTOCOPY_CENTER': 'business', 'PRINTER': 'business',
  'PUBLIC_ADDRESS_SYSTEM': 'business', 'SECRETARIAL_SERVICES': 'business', 'TELECONFERENCING_FACILITIES': 'business',
  /* Accessibility */
  'ACCESSIBLE': 'accessibility', 'ACCESSIBLE_BATHING_CONTROLS': 'accessibility', 'ACCESSIBLE_BATHS': 'accessibility',
  'ACCESSIBLE_ELEVATORS': 'accessibility', 'ACCESSIBLE_FACILITIES': 'accessibility', 'ACCESSIBLE_LIGHT_SWITCH': 'accessibility',
  'ACCESSIBLE_PARKING': 'accessibility', 'ACCESSIBLE_ROOM': 'accessibility', 'ACCESSIBLE_TOILETS': 'accessibility',
  'ACCESSIBLE_WASH_BASINS': 'accessibility', 'ADAPTED_BATHROOMS': 'accessibility', 'ADAPTED_ROOM_DOORS': 'accessibility',
  'BEDROOM_WHEELCHAIR_ACCESS': 'accessibility', 'DISABLED_FACILITIES': 'accessibility', 'DISABLED_PARKING': 'accessibility',
  'DOORS_HAVE_VIEWPORTS': 'accessibility', 'HANDICAP_ROOM': 'accessibility', 'HANDRAILS_IN_BATHROOMS': 'accessibility',
  'HEARING_INDUCTION_LOOPS': 'accessibility', 'MEETS_AMERICANS_WITH_DISABILITIES_ACT_OF_1990_SPECS': 'accessibility', 'ROOMS_ACCESSIBLE_FROM_THE_EXTERIOR': 'accessibility',
  'ROOMS_ACCESSIBLE_FROM_THE_INTERIOR': 'accessibility', 'STAFF_TRAINED_TO_CATER_FOR_DISABLED_GUESTS': 'accessibility', 'WHEELCHAIR_ENTRANCE': 'accessibility',
  'WIDE_CORRIDORS': 'accessibility', 'WIDE_ENTRANCE': 'accessibility', 'WIDE_RESTAURANT_ENTRANCE': 'accessibility',
  /* Safety & Security */
  'ALARMS_CONTINUOUSLY_MONITORED': 'safety', 'ALARMS_HAVE_STROBE_LIGHTS_IN_HALLWAYS': 'safety', 'ALARMS_HAVE_STROBE_LIGHTS_IN_PUBLIC_AREA': 'safety',
  'AUDIBLE_ALARMS': 'safety', 'AUDIBLE_SMOKE_ALARMS_HARDWIRED': 'safety', 'AUDIBLE_SMOKE_ALARMS_IN_HALLS': 'safety',
  'AUDIBLE_SMOKE_ALARMS_IN_PUBLIC_AREAS': 'safety', 'AUDIBLE_SMOKE_ALARMS_IN_ROOMS': 'safety', 'AUTOLINK_TO_FIRE_DEPARTMENT': 'safety',
  'AUTOMATED_EXTERNAL_DEFIBRILLATOR_ON_SITE_AED': 'safety', 'AUTOMATIC_FIRE_DOORS': 'safety', 'AUTO_RECALL_ELEVATORS': 'safety',
  'BASIC_MEDICAL_EQUIPMENT_ON_SITE': 'safety', 'DEADBOLTS_ON_ROOM_DOORS': 'safety', 'DOCTOR_ON_CALL': 'safety',
  'ELECTRIC_GENERATOR': 'safety', 'ELECTRONIC_KEY_CARDS': 'safety', 'EMERGENCY_CORDS_OR_BUTTONS': 'safety',
  'EMERGENCY_EVACUATION_DRILL_FREQ_YEARLY': 'safety', 'EMERGENCY_INFO_IN_ROOMS': 'safety', 'EMERGENCY_LIGHTING': 'safety',
  'ENHANCED_SAFETY_PROTOCOL': 'safety', 'EVACUATION_PLAN': 'safety', 'EXIT_SIGNS_LIT': 'safety',
  'EXTERIOR_DOORS_EXCEPT_LOBBY_ENTRANCE_OF_THE_FACILITY_REQUIRE_KEY_ACCESS_AT_NIGHT_OR_AUTOMATICALLY_LOCK': 'safety', 'FEMA_FIRE_SAFETYFACILITIES_COMPLIANT_IN_THE_USA_.': 'safety', 'FIRE_ALARM': 'safety',
  'FIRE_ALARM_WITH_LIGHT': 'safety', 'FIRE_DETECTORS': 'safety', 'FIRE_DETECTORS_IN_HALL': 'safety',
  'FIRE_DETECTORS_IN_ROOMS': 'safety', 'FIRE_EXTINGUISHERS_IN_HALLWAYS': 'safety', 'FIRE_EXTINGUISHERS_IN_PUBLIC_AREAS': 'safety',
  'FIRE_SAFETYFACILITIES': 'safety', 'FIRE_SAFETY_COMPLIANT': 'safety', 'FIRST_AID_AVAILABLE': 'safety',
  'GUARDED_CAR_PARKING': 'safety', 'GUEST_ROOM_DOORS_HAVE_A_SECOND_LOCK': 'safety', 'GUEST_ROOM_DOORS_SELF_CLOSING': 'safety',
  'HOURS_SECURITY': 'safety', 'MULTIPLE_EXITS_ON_EACH_FLOOR': 'safety', 'PARKING_AREAS_WELL_LIT': 'safety',
  'PROPERTY_MEETS_REQUIREMENTS_FOR_FIRE_SAFETY': 'safety', 'RESTRICTED_ACCESS_TO_ROOM_FLOORS': 'safety', 'SAFETY_CHAINS_ON_GUEST_ROOM_DOOR': 'safety',
  'SECURED_FLOORS_AVAILABLE': 'safety', 'SECURITY': 'safety', 'SECURITY_GUARDS': 'safety',
  'SECURITY_PERSONNEL_ON_SITE': 'safety', 'SMOKE_DETECTORS': 'safety', 'SPRINKLERS': 'safety',
  'SPRINKLERS_IN_HALL': 'safety', 'SPRINKLERS_IN_ROOMS': 'safety', 'STAFF_TRAINED_FOR_DUPLICATE_KEYS': 'safety',
  'STAFF_TRAINED_IN_AUTOMATED_EXTERNAL_DEFIBRILLATOR_USAGE_AED': 'safety', 'STAFF_TRAINED_IN_FIRST_AID_TECHNIQUES': 'safety', 'VENTILATED_STAIR_WELLS': 'safety',
  'VIDEO_SURVEILLANCE_IN_HALLWAYS': 'safety', 'VIDEO_SURVEILLANCE_IN_PUBLIC_AREAS': 'safety', 'VIDEO_SURVEILLANCE_OF_PARKING_AREAS': 'safety',
  'VIDEO_SURVEILLANCE_RECORDED_24_HRS_A_DAY': 'safety', 'WELL_LIT_WALKWAYS': 'safety',
};
export function amenityCategory(code: string): string { return AMENITY_CAT[code] ?? 'general'; }
